import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PercentIcon from "@mui/icons-material/Percent";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CategoryIcon from "@mui/icons-material/Category";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import moment from "moment";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { AuthState } from "context/AuthContext";
import { Discount_Master_Brands, DISOUNT_MASTER_CHANNEL_OPTIONS } from "../data";
import { roleAccess } from "../DiscountMasterList";
import { D3BarChart, D3DonutChart, D3LineChart, D3StackedBarChart } from "./D3Charts";

const ALL_CHANNEL_OPTIONS = DISOUNT_MASTER_CHANNEL_OPTIONS.map((c) => c.key);
const ALL_BRAND_OPTIONS = Discount_Master_Brands.map((b) => b.key);
const ALL_DIVISION_OPTIONS = ["APPARELS", "ADDONS", "FOOT WEAR"];

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const MultiSelectFilter = ({
	label,
	options = [],
	value = [],
	onChange,
	emptyLabel = "All accessible",
}) => {
	const allSelected = options.length > 0 && options.every((opt) => value.includes(opt));
	const someSelected = value.length > 0 && !allSelected;

	const handleChange = (event) => {
		const next = event.target.value;
		if (!Array.isArray(next)) return;

		if (next.includes(SELECT_ALL_VALUE)) {
			onChange(allSelected ? [] : [...options]);
			return;
		}
		onChange(next.filter((item) => item !== SELECT_ALL_VALUE));
	};

	return (
		<FormControl fullWidth size="small">
			<InputLabel>{label}</InputLabel>
			<Select
				multiple
				label={label}
				value={value}
				onChange={handleChange}
				renderValue={(selected) => {
					if (!selected.length) return emptyLabel;
					if (selected.length === options.length) return `All (${options.length})`;
					if (selected.length <= 2) return selected.join(", ");
					return `${selected.length} selected`;
				}}
				MenuProps={{
					PaperProps: { sx: { maxHeight: 360 } },
					autoFocus: false,
				}}>
				<MenuItem
					value={SELECT_ALL_VALUE}
					dense
					sx={{
						borderBottom: "1px solid #eee",
						position: "sticky",
						top: 0,
						bgcolor: "#fff",
						zIndex: 1,
						"&.Mui-selected": { bgcolor: "#fff" },
						"&.Mui-selected:hover": { bgcolor: "#f5f5f5" },
					}}>
					<Checkbox
						size="small"
						checked={allSelected}
						indeterminate={someSelected}
						sx={{ py: 0.25, pl: 0 }}
					/>
					<ListItemText
						primary="Select All"
						primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
					/>
				</MenuItem>
				{options.map((opt) => (
					<MenuItem key={opt} value={opt} dense>
						<Checkbox size="small" checked={value.includes(opt)} sx={{ py: 0.25, pl: 0 }} />
						<ListItemText primary={opt} primaryTypographyProps={{ fontSize: 13 }} />
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

const formatINR = (value) => {
	const num = Number(value) || 0;
	if (Math.abs(num) >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
	if (Math.abs(num) >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
	return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const formatNumber = (value) =>
	(Number(value) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const formatPct = (value) => `${(Number(value) || 0).toFixed(1)}%`;

const CHART_COLORS = [
	"#2563EB", // strong blue
	"#059669", // strong emerald
	"#D97706", // strong amber
	"#7C3AED", // strong violet
	"#0891B2", // strong cyan
	"#DC2626", // strong red
	"#DB2777", // strong pink
	"#0D9488", // strong teal
];

const SCHEME_BAND_COLORS = [
	"#2563EB55",
	"#05966955",
	"#D9770655",
	"#7C3AED55",
	"#0891B255",
	"#DC262655",
	"#DB277755",
];

const SCHEME_BAND_BORDER = [
	"#1D4ED8",
	"#047857",
	"#B45309",
	"#6D28D9",
	"#0E7490",
	"#B91C1C",
	"#BE185D",
];

/** Vibrant graph colors only */
const PASTEL = {
	sale: "#2563EB",
	discount: "#DC2626",
	good: "#059669",
	warn: "#D97706",
	purple: "#7C3AED",
	teal: "#0D9488",
	pink: "#DB2777",
	ebo: "#2563EB",
	lfs: "#059669",
	mbo: "#D97706",
};

const BRAND_LINE_COLORS = {
	"RARE RABBIT": "#2563EB",
	RAREISM: "#059669",
	"RARE ONES": "#D97706",
	RAREZ: "#7C3AED",
};

const escapeHtml = (value) =>
	String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");

/** Rich hover card for scheme charts (absorbed % of MRP) */
const buildSchemeTooltip = (row, highlightLabel, highlightValue) => {
	const name = escapeHtml(row?.scheme_name || "Scheme");
	const action = getAbsorbedSchemeAction(row?.catalog_discount_pct);
	const ofMrp = discountPctOfMrp(row);
	const lines = [
		["Channel", row?.channel || "—"],
		["Sale", formatINR(row?.net_sale)],
		["Discount given", formatINR(row?.absorbed_discount)],
		["Discount % of MRP", ofMrp == null ? "—" : formatPct(ofMrp)],
	];
	if (row?.contribution_pct != null) {
		lines.push(["Sale share", formatPct(row.contribution_pct)]);
	}
	if (row?.sale_per_day != null) {
		lines.push(["Sale / day", formatINR(row.sale_per_day)]);
	}
	if (highlightLabel && highlightValue != null) {
		lines.unshift([highlightLabel, highlightValue]);
	}
	return `
		<div style="font-weight:700;font-size:13px;margin-bottom:6px;word-break:break-word">${name}</div>
		${lines
			.map(
				([k, v]) =>
					`<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">${escapeHtml(k)}</span>
						<strong>${escapeHtml(v)}</strong>
					</div>`
			)
			.join("")}
		<div style="margin-top:8px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.12);color:${action.color};font-weight:600">
			${escapeHtml(action.label)} · ${escapeHtml(action.hint)}
		</div>`;
};

/**
 * Recommendation from absorbed discount (% off MRP).
 * Lower off-MRP % = healthier scheme for Decide tab.
 */
const getAbsorbedSchemeAction = (absorbedDiscountPct) => {
	const pct = Number(absorbedDiscountPct);
	if (!pct || Number.isNaN(pct) || pct <= 0) {
		return { label: "Monitor", color: "#8FA0B5", hint: "Not enough data yet" };
	}
	if (pct <= 12.5) {
		return {
			label: "Keep & grow",
			color: PASTEL.good,
			hint: "Low absorbed % — consider extending",
		};
	}
	if (pct >= 33) {
		return {
			label: "Review / cut",
			color: PASTEL.discount,
			hint: "Deep absorbed discount — tighten or stop",
		};
	}
	return {
		label: "Tune discount",
		color: PASTEL.warn,
		hint: "Moderate absorbed % — optimize depth or days",
	};
};

const truncateLabel = (label, max = 18) => {
	const text = String(label || "");
	return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const ChangeChip = ({ value, suffix = "%" }) => {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return null;
	}
	const num = Number(value);
	const positive = num >= 0;
	return (
		<Chip
			size="small"
			icon={positive ? <TrendingUpIcon /> : <TrendingDownIcon />}
			label={`${positive ? "+" : ""}${num.toFixed(1)}${suffix}`}
			sx={{
				height: 22,
				fontSize: 11,
				maxWidth: "100%",
				bgcolor: positive ? "#E8F5EE" : "#FCEAEA",
				color: positive ? PASTEL.good : PASTEL.discount,
				"& .MuiChip-icon": { color: "inherit", fontSize: 14 },
				"& .MuiChip-label": { px: 0.75 },
			}}
		/>
	);
};

const KpiCard = ({ title, value, subtitle, change, changeSuffix = "%", icon, accent = PASTEL.sale }) => (
	<Paper
		elevation={0}
		sx={{
			p: 1.25,
			height: "100%",
			minHeight: 88,
			width: "100%",
			borderRadius: 1.5,
			border: "1px solid #e8eaed",
			background: `linear-gradient(135deg, ${accent}06 0%, #fff 60%)`,
			overflow: "hidden",
			boxSizing: "border-box",
		}}>
		<Stack spacing={0.35} height="100%" justifyContent="space-between">
			<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.75}>
				<Typography
					variant="caption"
					color="text.secondary"
					fontWeight={500}
					sx={{ lineHeight: 1.25, pr: 0.5, fontSize: 12 }}>
					{title}
				</Typography>
				<Box
					sx={{
						width: 24,
						height: 24,
						flexShrink: 0,
						borderRadius: 1,
						display: "grid",
						placeItems: "center",
						bgcolor: `${accent}12`,
						color: accent,
						"& .MuiSvgIcon-root": { fontSize: 15 },
					}}>
					{icon}
				</Box>
			</Stack>
			<Box>
				<Typography
					sx={{
						fontSize: 17,
						fontWeight: 600,
						color: "#455a64",
						lineHeight: 1.15,
						wordBreak: "break-word",
					}}>
					{value}
				</Typography>
				{subtitle ? (
					<Typography
						variant="caption"
						color="text.secondary"
						noWrap
						title={subtitle}
						sx={{ display: "block", mt: 0.15, fontSize: 11, lineHeight: 1.3 }}>
						{subtitle}
					</Typography>
				) : null}
				{change !== undefined ? (
					<Box mt={0.35}>
						<ChangeChip value={change} suffix={changeSuffix} />
					</Box>
				) : null}
			</Box>
		</Stack>
	</Paper>
);

const ChartCard = ({ title, subtitle = null, children, chartHeight = 280, footer = null }) => (
	<Paper
		elevation={0}
		sx={{
			p: 1.25,
			borderRadius: 2,
			border: "1px solid #e8eaed",
			width: "100%",
			maxWidth: "100%",
			minWidth: 0,
			position: "relative",
			bgcolor: "#fff",
			overflow: "hidden",
			boxSizing: "border-box",
		}}>
		<Box mb={1.25} sx={{ minWidth: 0 }}>
			<Typography
				sx={{
					fontSize: 14,
					fontWeight: 500,
					color: "#455a64",
					letterSpacing: "-0.01em",
					lineHeight: 1.3,
				}}>
				{title}
			</Typography>
			{subtitle ? (
				<Typography sx={{ mt: 0.25, fontSize: 12, color: "#90a4ae", lineHeight: 1.4, fontWeight: 400 }}>
					{subtitle}
				</Typography>
			) : null}
		</Box>
		<Box
			sx={{
				position: "relative",
				width: "100%",
				maxWidth: "100%",
				minWidth: 0,
				minHeight: chartHeight,
				overflow: "hidden",
			}}>
			{children}
		</Box>
		{footer ? <Box sx={{ minWidth: 0, maxWidth: "100%" }}>{footer}</Box> : null}
	</Paper>
);

const EmptyChart = ({ message }) => (
	<Box
		sx={{
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		}}>
		<Typography color="text.secondary" textAlign="center">
			{message}
		</Typography>
	</Box>
);

const SkeletonCard = ({ children, sx = {} }) => (
	<Paper
		elevation={0}
		sx={{
			p: 2,
			borderRadius: 2,
			border: "1px solid #e8eaed",
			width: "100%",
			maxWidth: "100%",
			minWidth: 0,
			boxSizing: "border-box",
			overflow: "hidden",
			...sx,
		}}>
		{children}
	</Paper>
);

const ReportLoadingSkeleton = () => (
	<Paper
		elevation={0}
		sx={{
			borderRadius: 2,
			border: "1px solid #e8eaed",
			overflow: "hidden",
			width: "100%",
			maxWidth: "100%",
			minWidth: 0,
			boxSizing: "border-box",
		}}>
		{/* Tabs */}
		<Box
			sx={{
				px: 2,
				py: 1.5,
				borderBottom: "1px solid #e8eaed",
				display: "flex",
				flexWrap: "wrap",
				gap: 1,
			}}>
			{[110, 100, 130, 90, 100].map((w) => (
				<Skeleton key={w} variant="rounded" width={w} height={28} sx={{ flexShrink: 0 }} />
			))}
		</Box>

		<Box px={{ xs: 1.5, md: 2 }} py={2} sx={{ minWidth: 0 }}>
			<Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
				{/* KPI row — 7 in a row on lg+ */}
				<Box
					sx={{
						display: "grid",
						gap: 1,
						width: "100%",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "1fr 1fr",
							md: "repeat(4, 1fr)",
							lg: "repeat(7, 1fr)",
						},
					}}>
					{Array.from({ length: 7 }).map((_, idx) => (
						<SkeletonCard key={`kpi-sk-${idx}`} sx={{ p: 1.25, minHeight: 88 }}>
							<Stack spacing={0.5} sx={{ minWidth: 0 }}>
								<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
									<Skeleton variant="text" width="55%" height={14} sx={{ minWidth: 0 }} />
									<Skeleton variant="circular" width={22} height={22} sx={{ flexShrink: 0 }} />
								</Stack>
								<Skeleton variant="text" width="70%" height={22} />
								<Skeleton variant="text" width="40%" height={12} />
							</Stack>
						</SkeletonCard>
					))}
				</Box>

				{/* Trend + pie */}
				<Grid container spacing={1.5} sx={{ width: "100%", margin: 0 }}>
					<Grid item xs={12} lg={8} sx={{ minWidth: 0 }}>
						<SkeletonCard>
							<Skeleton variant="text" width="42%" height={20} sx={{ mb: 1.5 }} />
							<Skeleton variant="rounded" width="100%" height={220} />
						</SkeletonCard>
					</Grid>
					<Grid item xs={12} lg={4} sx={{ minWidth: 0 }}>
						<SkeletonCard>
							<Skeleton variant="text" width="48%" height={20} sx={{ mb: 2 }} />
							<Box display="flex" justifyContent="center" alignItems="center" py={2}>
								<Skeleton variant="circular" width={140} height={140} />
							</Box>
							<Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
								<Skeleton variant="rounded" width={70} height={14} />
								<Skeleton variant="rounded" width={70} height={14} />
								<Skeleton variant="rounded" width={70} height={14} />
							</Stack>
						</SkeletonCard>
					</Grid>
				</Grid>

				{/* Channel discount charts */}
				<Box sx={{ width: "100%", minWidth: 0 }}>
					<Skeleton variant="text" width={220} height={22} sx={{ mb: 0.5 }} />
					<Skeleton variant="text" width="55%" height={14} sx={{ mb: 1.5 }} />
					<Stack spacing={1.5}>
						{[0, 1].map((i) => (
							<SkeletonCard key={`ch-sk-${i}`}>
								<Skeleton variant="text" width="38%" height={20} sx={{ mb: 1.5 }} />
								<Skeleton variant="rounded" width="100%" height={200} />
								<Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
									<Skeleton variant="rounded" width={140} height={22} />
									<Skeleton variant="rounded" width={120} height={22} />
								</Stack>
							</SkeletonCard>
						))}
					</Stack>
				</Box>

				{/* Scheme sale bar chart */}
				<SkeletonCard>
					<Skeleton variant="text" width="46%" height={20} sx={{ mb: 1.5 }} />
					<Stack spacing={1.25}>
						{Array.from({ length: 5 }).map((_, idx) => (
							<Stack
								key={`bar-sk-${idx}`}
								direction="row"
								spacing={1}
								alignItems="center"
								sx={{ width: "100%", minWidth: 0 }}>
								<Skeleton variant="rounded" width={96} height={14} sx={{ flexShrink: 0 }} />
								<Skeleton
									variant="rounded"
									height={14}
									sx={{
										flex: 1,
										minWidth: 0,
										maxWidth: `${Math.max(28, 72 - idx * 10)}%`,
									}}
								/>
							</Stack>
						))}
					</Stack>
				</SkeletonCard>
			</Stack>
		</Box>
	</Paper>
);

const TabPanel = ({ value, index, children }) => {
	if (value !== index) return null;
	return (
		<Box
			sx={{
				pt: 2,
				position: "relative",
				width: "100%",
				maxWidth: "100%",
				minWidth: 0,
				overflow: "visible",
				boxSizing: "border-box",
			}}>
			{children}
		</Box>
	);
};

const gridItemSx = { minWidth: 0, maxWidth: "100%" };
const gridContainerSx = { width: "100%", maxWidth: "100%", margin: 0 };

const REPORT_TABS = [
	{ key: "overview", label: "Overview", icon: <ShowChartIcon fontSize="small" /> },
	{ key: "channel_brand", label: "Channel & Brand", icon: <CategoryIcon fontSize="small" /> },
	{ key: "stores", label: "Stores", icon: <StorefrontIcon fontSize="small" /> },
	{ key: "schemes", label: "Schemes", icon: <LocalOfferIcon fontSize="small" /> },
];

const emptyReport = {
	kpis: {},
	kpi_changes: {},
	trend: [],
	trend_by_channel: [],
	channel_frame_trend: [],
	sales_by_channel: [],
	sales_by_brand: [],
	sales_by_channel_brand: [],
	top_stores: [],
	scheme_frame_sales: [],
	brand_daily: [],
	insights: {
		kpis: {},
		top_efficient_schemes: [],
		weak_efficient_schemes: [],
		top_sale_schemes: [],
		channel_intensity: [],
		brand_intensity: [],
	},
	schemes: {
		by_status: [],
		by_channel: [],
		expiring_soon: [],
		recent_schemes: [],
		schemes: [],
	},
	errors: {},
};

const buildContinuousDates = (startIso, endIso) => {
	const dates = [];
	let cursor = moment(startIso).startOf("day");
	const end = moment(endIso).startOf("day");
	if (!cursor.isValid() || !end.isValid() || end.isBefore(cursor)) return dates;
	while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
		dates.push(cursor.format("YYYY-MM-DD"));
		cursor = cursor.clone().add(1, "day");
	}
	return dates;
};

/** Normalize API / Date / ISO values to YYYY-MM-DD for chart date keys.
 *  Prefer the calendar date prefix so close_date / midnight UTC values don't shift a day.
 */
const toDateKey = (value) => {
	if (!value) return null;
	if (typeof value === "string") {
		const trimmed = value.trim();
		const m = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
		if (m) return m[1];
	}
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		const y = value.getFullYear();
		const mo = String(value.getMonth() + 1).padStart(2, "0");
		const d = String(value.getDate()).padStart(2, "0");
		return `${y}-${mo}-${d}`;
	}
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const buildContinuousMonths = (startIso, endIso) => {
	const months = [];
	let cursor = moment(startIso).startOf("month");
	const end = moment(endIso).startOf("month");
	if (!cursor.isValid() || !end.isValid() || end.isBefore(cursor)) return months;
	while (cursor.isBefore(end) || cursor.isSame(end, "month")) {
		months.push(cursor.format("YYYY-MM"));
		cursor = cursor.clone().add(1, "month");
	}
	return months;
};

/** True when From–To spans more than 3 calendar months (exclusive of a 3-month window). */
const isMonthWiseRange = (startIso, endIso) => {
	const start = moment(startIso).startOf("day");
	const end = moment(endIso).startOf("day");
	if (!start.isValid() || !end.isValid() || end.isBefore(start)) return false;
	return end.diff(start, "months", true) > 3;
};

const emptyMetricBucket = () => ({
	net_sale: 0,
	mrp_sale: 0,
	absorbed_discount: 0,
	qty_sold: 0,
	bill_count: 0,
});

const addMetrics = (bucket, row) => {
	bucket.net_sale += Number(row?.net_sale) || 0;
	bucket.mrp_sale += Number(row?.mrp_sale) || 0;
	bucket.absorbed_discount += Number(row?.absorbed_discount) || 0;
	bucket.qty_sold += Number(row?.qty_sold) || 0;
	bucket.bill_count += Number(row?.bill_count) || 0;
	return bucket;
};

const metricsFromBucket = (bucket) => {
	const net = bucket.net_sale || 0;
	const mrp = bucket.mrp_sale || 0;
	const absorbed = bucket.absorbed_discount || 0;
	const qty = bucket.qty_sold || 0;
	const bills = bucket.bill_count || 0;
	return {
		net_sale: net,
		mrp_sale: mrp,
		absorbed_discount: absorbed,
		qty_sold: qty,
		bill_count: bills,
		catalog_discount_pct: mrp > 0 ? (absorbed / mrp) * 100 : 0,
		asp: qty > 0 ? net / qty : 0,
		aov: bills > 0 ? net / bills : 0,
	};
};

/** Discount % of MRP for a day/period row: absorbed ÷ MRP */
const discountPctOfMrp = (row) => {
	if (row?.catalog_discount_pct != null && row.catalog_discount_pct !== "") {
		return Number(row.catalog_discount_pct);
	}
	const mrp = Number(row?.mrp_sale) || 0;
	const absorbed = Number(row?.absorbed_discount) || 0;
	if (mrp <= 0) return null;
	return (absorbed / mrp) * 100;
};

/** One graph per channel × brand. Sale line always; discount bands only if schemes match. */
const ChannelBrandSaleLineChart = ({
	channel,
	brand,
	brandDaily = [],
	schemeFrames = [],
	rangeStart,
	rangeEnd,
}) => {
	const chart = useMemo(() => {
		const channelKey = String(channel || "").toUpperCase();
		const brandKey = String(brand || "").toUpperCase();

		const brandRows = (brandDaily || []).filter(
			(r) =>
				String(r.channel || "").toUpperCase() === channelKey &&
				String(r.brand || "").trim().toUpperCase() === brandKey
		);

		// Discount bands only for schemes on this channel that include this brand
		const matchingFrames = (schemeFrames || []).filter((f) => {
			if (String(f.channel || "").toUpperCase() !== channelKey) return false;
			const frameBrands = Array.isArray(f.brands)
				? f.brands.map((b) => String(b).trim().toUpperCase())
				: [];
			return frameBrands.includes(brandKey);
		});

		let dateList = [];
		const rangeFrom = toDateKey(rangeStart);
		const rangeTo = toDateKey(rangeEnd);
		if (rangeFrom && rangeTo) {
			dateList = buildContinuousDates(rangeFrom, rangeTo);
		} else {
			const sorted = brandRows
				.map((r) => toDateKey(r.bill_date || r.date))
				.filter(Boolean)
				.sort();
			if (sorted.length) {
				dateList = buildContinuousDates(sorted[0], sorted[sorted.length - 1]);
			}
		}

		const saleMap = {};
		brandRows.forEach((row) => {
			const key = toDateKey(row.bill_date || row.date);
			if (!key) return;
			saleMap[key] = (saleMap[key] || 0) + (Number(row.net_sale) || 0);
		});

		const values = dateList.map((d) => saleMap[d] || 0);
		const labels = dateList.map((d) => moment(d).format("DD MMM"));
		const lineColor =
			BRAND_LINE_COLORS[brandKey] ||
			{ EBO: PASTEL.ebo, LFS: PASTEL.lfs, MBO: PASTEL.mbo }[channelKey] ||
			PASTEL.sale;

		const tooltips = dateList.map((d, i) => {
			const dateLabel = escapeHtml(moment(d).format("DD MMM YYYY"));
			const activeBands = matchingFrames.filter((f) => {
				const startKey = toDateKey(f.frame_start);
				const endKey = toDateKey(f.frame_end);
				return startKey && endKey && d >= startKey && d <= endKey;
			});
			const discountRows = activeBands
				.map(
					(f) => `
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">${escapeHtml(f.scheme_name || "Scheme")}</span>
					<strong>${escapeHtml(formatPct(discountPctOfMrp(f) ?? 0))} MRP</strong>
				</div>`
				)
				.join("");
			return `
				<div style="font-weight:700;margin-bottom:4px">${escapeHtml(channelKey)} · ${escapeHtml(
				brandKey
			)} · ${dateLabel}</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">Net sale</span>
					<strong>${escapeHtml(formatINR(values[i]))}</strong>
				</div>
				${
					activeBands.length
						? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.12);color:#90a4ae;font-size:11px">Active discounts</div>${discountRows}`
						: `<div style="margin-top:6px;color:#90a4ae;font-size:11px">No discount on this day</div>`
				}`;
		});

		const bands = matchingFrames
			.map((frame, idx) => {
				const startKey = toDateKey(frame.frame_start);
				const endKey = toDateKey(frame.frame_end);
				if (!startKey || !endKey || !dateList.length) return null;
				let startIdx = dateList.indexOf(startKey);
				let endIdx = dateList.indexOf(endKey);
				if (startIdx < 0) startIdx = dateList.findIndex((d) => d >= startKey);
				if (endIdx < 0) {
					for (let i = dateList.length - 1; i >= 0; i -= 1) {
						if (dateList[i] <= endKey) {
							endIdx = i;
							break;
						}
					}
				}
				if (startIdx < 0 || endIdx < 0 || endIdx < startIdx) return null;
				const ofMrp = discountPctOfMrp(frame);
				const detailHtml = `
					<div style="margin-left:12px;margin-top:2px;font-size:11px">
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">Channel · Brand</span>
							<span style="color:#fff">${escapeHtml(`${channelKey} · ${brandKey}`)}</span>
						</div>
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">Sale</span>
							<span style="color:#fff">${escapeHtml(formatINR(frame.net_sale))}</span>
						</div>
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">Discount given</span>
							<span style="color:#fff">${escapeHtml(formatINR(frame.absorbed_discount))}</span>
						</div>
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">Discount % of MRP</span>
							<span style="color:#fff">${escapeHtml(ofMrp == null ? "—" : formatPct(ofMrp))}</span>
						</div>
					</div>`;
				return {
					id: `${frame.scheme_id}-${channelKey}-${brandKey}`,
					name: frame.scheme_name || "Scheme",
					startLabel: moment(startKey).format("DD MMM"),
					endLabel: moment(endKey).format("DD MMM"),
					startIdx,
					endIdx,
					bg: SCHEME_BAND_COLORS[idx % SCHEME_BAND_COLORS.length],
					border: SCHEME_BAND_BORDER[idx % SCHEME_BAND_BORDER.length],
					detailHtml,
				};
			})
			.filter(Boolean);

		const hasSale = values.some((v) => v > 0);
		return {
			labels,
			values,
			tooltips,
			bands,
			lineColor,
			hasSale,
			hasDiscount: bands.length > 0,
		};
	}, [channel, brand, brandDaily, schemeFrames, rangeStart, rangeEnd]);

	const title = `${channel} · ${brand}`;

	if (!chart.labels.length || !chart.hasSale) {
		return null;
	}

	return (
		<ChartCard
			title={title}
			subtitle={
				chart.hasDiscount
					? "Sale line with discount period colors (schemes for this channel + brand)."
					: "Sale line only — no discount scheme for this channel + brand in the selected period."
			}
			chartHeight={260}
			footer={
				chart.hasDiscount ? (
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1.5}>
						{chart.bands.map((band) => (
							<Chip
								key={band.id}
								size="small"
								title={`${band.name} · ${band.startLabel}–${band.endLabel}`}
								sx={{
									height: 26,
									borderRadius: 1.5,
									bgcolor: band.bg,
									border: `1px solid ${band.border}55`,
									color: band.border,
									fontWeight: 500,
									fontSize: 11,
									maxWidth: "100%",
									"& .MuiChip-label": { px: 1 },
								}}
								label={`${truncateLabel(band.name, 22)}  ·  ${band.startLabel}–${band.endLabel}`}
							/>
						))}
					</Stack>
				) : null
			}>
			<D3LineChart
				labels={chart.labels}
				series={[{ label: "Net sale", color: chart.lineColor, values: chart.values }]}
				tooltips={chart.tooltips}
				bands={chart.bands}
				height={240}
				valueFormatter={formatINR}
			/>
		</ChartCard>
	);
};

/** Day-wise ROS / AOV / ASP for one channel × brand. Discount bands only if schemes match. */
const ChannelBrandRosAovAspLineChart = ({
	channel,
	brand,
	brandDaily = [],
	schemeFrames = [],
	rangeStart,
	rangeEnd,
}) => {
	const chart = useMemo(() => {
		const channelKey = String(channel || "").toUpperCase();
		const brandKey = String(brand || "").toUpperCase();

		const brandRows = (brandDaily || []).filter(
			(r) =>
				String(r.channel || "").toUpperCase() === channelKey &&
				String(r.brand || "").trim().toUpperCase() === brandKey
		);

		const matchingFrames = (schemeFrames || []).filter((f) => {
			if (String(f.channel || "").toUpperCase() !== channelKey) return false;
			const frameBrands = Array.isArray(f.brands)
				? f.brands.map((b) => String(b).trim().toUpperCase())
				: [];
			return frameBrands.includes(brandKey);
		});

		let dateList = [];
		const rangeFrom = toDateKey(rangeStart);
		const rangeTo = toDateKey(rangeEnd);
		if (rangeFrom && rangeTo) {
			dateList = buildContinuousDates(rangeFrom, rangeTo);
		} else {
			const sorted = brandRows
				.map((r) => toDateKey(r.bill_date || r.date))
				.filter(Boolean)
				.sort();
			if (sorted.length) {
				dateList = buildContinuousDates(sorted[0], sorted[sorted.length - 1]);
			}
		}

		const byDate = {};
		brandRows.forEach((row) => {
			const key = toDateKey(row.bill_date || row.date);
			if (!key) return;
			const net = Number(row.net_sale) || 0;
			const qty = Number(row.qty_sold) || 0;
			const bills = Number(row.bill_count) || 0;
			const prev = byDate[key] || { net: 0, qty: 0, bills: 0 };
			byDate[key] = {
				net: prev.net + net,
				qty: prev.qty + qty,
				bills: prev.bills + bills,
			};
		});
		Object.keys(byDate).forEach((key) => {
			const row = byDate[key];
			row.ros = row.qty;
			row.asp = row.qty > 0 ? row.net / row.qty : 0;
			row.aov = row.bills > 0 ? row.net / row.bills : 0;
		});

		const labels = dateList.map((d) => moment(d).format("DD MMM"));
		const ros = dateList.map((d) => byDate[d]?.ros || 0);
		const asp = dateList.map((d) => byDate[d]?.asp || 0);
		const aov = dateList.map((d) => byDate[d]?.aov || 0);
		const hasData = ros.some((v) => v > 0) || asp.some((v) => v > 0) || aov.some((v) => v > 0);

		const tooltips = dateList.map((d, i) => {
			const row = byDate[d];
			const dateLabel = escapeHtml(moment(d).format("DD MMM YYYY"));
			const activeBands = matchingFrames.filter((f) => {
				const startKey = toDateKey(f.frame_start);
				const endKey = toDateKey(f.frame_end);
				return startKey && endKey && d >= startKey && d <= endKey;
			});
			return `
				<div style="font-weight:700;margin-bottom:4px">${escapeHtml(channelKey)} · ${escapeHtml(
				brandKey
			)} · ${dateLabel}</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">ROS (units)</span><strong>${escapeHtml(
						formatNumber(ros[i])
					)}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">ASP</span><strong>${escapeHtml(formatINR(asp[i]))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">AOV</span><strong>${escapeHtml(formatINR(aov[i]))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">Net sale</span><strong>${escapeHtml(
						formatINR(row?.net)
					)}</strong>
				</div>
				${
					activeBands.length
						? `<div style="margin-top:6px;color:#90a4ae;font-size:11px">${activeBands.length} active discount(s)</div>`
						: `<div style="margin-top:6px;color:#90a4ae;font-size:11px">No discount on this day</div>`
				}`;
		});

		const bands = matchingFrames
			.map((frame, idx) => {
				const startKey = toDateKey(frame.frame_start);
				const endKey = toDateKey(frame.frame_end);
				if (!startKey || !endKey || !dateList.length) return null;
				let startIdx = dateList.indexOf(startKey);
				let endIdx = dateList.indexOf(endKey);
				if (startIdx < 0) startIdx = dateList.findIndex((d) => d >= startKey);
				if (endIdx < 0) {
					for (let i = dateList.length - 1; i >= 0; i -= 1) {
						if (dateList[i] <= endKey) {
							endIdx = i;
							break;
						}
					}
				}
				if (startIdx < 0 || endIdx < 0 || endIdx < startIdx) return null;
				const ofMrp = discountPctOfMrp(frame);
				const detailHtml = `
					<div style="margin-left:12px;margin-top:2px;font-size:11px">
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">ROS</span>
							<span style="color:#fff">${escapeHtml(formatNumber(frame.ros))}/day</span>
						</div>
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">ASP</span>
							<span style="color:#fff">${escapeHtml(formatINR(frame.asp))}</span>
						</div>
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">AOV</span>
							<span style="color:#fff">${escapeHtml(formatINR(frame.aov))}</span>
						</div>
						<div style="display:flex;justify-content:space-between;gap:12px">
							<span style="color:#90a4ae">Discount % of MRP</span>
							<span style="color:#fff">${escapeHtml(ofMrp == null ? "—" : formatPct(ofMrp))}</span>
						</div>
					</div>`;
				return {
					id: `${frame.scheme_id}-ros-${channelKey}-${brandKey}`,
					name: frame.scheme_name || "Scheme",
					startLabel: moment(startKey).format("DD MMM"),
					endLabel: moment(endKey).format("DD MMM"),
					startIdx,
					endIdx,
					bg: SCHEME_BAND_COLORS[idx % SCHEME_BAND_COLORS.length],
					border: SCHEME_BAND_BORDER[idx % SCHEME_BAND_BORDER.length],
					detailHtml,
				};
			})
			.filter(Boolean);

		return {
			labels,
			ros,
			asp,
			aov,
			tooltips,
			bands,
			hasData,
			hasDiscount: bands.length > 0,
		};
	}, [channel, brand, brandDaily, schemeFrames, rangeStart, rangeEnd]);

	const title = `${channel} · ${brand} — ROS · AOV · ASP`;

	if (!chart.labels.length || !chart.hasData) {
		return null;
	}

	return (
		<ChartCard
			title={title}
			subtitle={
				chart.hasDiscount
					? "Day-wise ROS / ASP / AOV with discount period colors for this channel + brand."
					: "Day-wise ROS / ASP / AOV — no discount scheme for this channel + brand."
			}
			chartHeight={280}
			footer={
				chart.hasDiscount ? (
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1.5}>
						{chart.bands.map((band) => (
							<Chip
								key={band.id}
								size="small"
								title={`${band.name} · ${band.startLabel}–${band.endLabel}`}
								sx={{
									height: 26,
									borderRadius: 1.5,
									bgcolor: band.bg,
									border: `1px solid ${band.border}55`,
									color: band.border,
									fontWeight: 500,
									fontSize: 11,
									maxWidth: "100%",
									"& .MuiChip-label": { px: 1 },
								}}
								label={`${truncateLabel(band.name, 20)}  ·  ${band.startLabel}–${band.endLabel}`}
							/>
						))}
					</Stack>
				) : null
			}>
			<D3LineChart
				height={240}
				labels={chart.labels}
				tooltips={chart.tooltips}
				bands={chart.bands}
				series={[
					{ label: "ASP", color: PASTEL.good, values: chart.asp },
					{ label: "AOV", color: PASTEL.sale, values: chart.aov },
					{ label: "ROS (units)", color: PASTEL.warn, values: chart.ros },
				]}
				valueFormatter={(v) => formatNumber(Math.round(Number(v) || 0))}
			/>
		</ChartCard>
	);
};


function DiscountReport() {
	const { user } = AuthState();
	const roleData = useMemo(() => roleAccess(user), [user]);

	const accessibleBrands = useMemo(() => {
		const allowed = roleData?.brandList || [];
		return ALL_BRAND_OPTIONS.filter((b) => allowed.includes(b));
	}, [roleData?.brandList]);

	const accessibleChannels = useMemo(() => {
		const allowed = roleData?.channelList || [];
		return ALL_CHANNEL_OPTIONS.filter((c) => allowed.includes(c));
	}, [roleData?.channelList]);

	const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
	const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
	const [brands, setBrands] = useState([]);
	const [channels, setChannels] = useState([]);
	const [divisions, setDivisions] = useState([...ALL_DIVISION_OPTIONS]);
	const [loading, setLoading] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [report, setReport] = useState(emptyReport);
	const [error, setError] = useState("");
	const [activeTab, setActiveTab] = useState(0);
	const initialLoadRef = useRef(false);

	// Seed brand/channel from user access once available
	useEffect(() => {
		if (accessibleBrands.length) {
			setBrands((prev) => (prev.length ? prev.filter((b) => accessibleBrands.includes(b)) : [...accessibleBrands]));
		}
		if (accessibleChannels.length) {
			setChannels((prev) =>
				prev.length ? prev.filter((c) => accessibleChannels.includes(c)) : [...accessibleChannels]
			);
		}
	}, [accessibleBrands, accessibleChannels]);

	const requestBrands = useMemo(() => {
		if (brands.length) {
			return brands.filter((b) => accessibleBrands.includes(b));
		}
		return accessibleBrands;
	}, [brands, accessibleBrands]);

	const requestChannels = useMemo(() => {
		if (channels.length) {
			return channels.filter((c) => accessibleChannels.includes(c));
		}
		return accessibleChannels;
	}, [channels, accessibleChannels]);

	const requestDivisions = useMemo(() => {
		if (divisions.length) {
			return divisions.filter((d) => ALL_DIVISION_OPTIONS.includes(d));
		}
		return [...ALL_DIVISION_OPTIONS];
	}, [divisions]);

	const hasAccess = requestBrands.length > 0 && requestChannels.length > 0;

	const fetchReport = useCallback(
		(forceRefresh = false) => {
			if (!hasAccess) {
				setError("No brand/channel access assigned for Discount Report.");
				setReport(emptyReport);
				setHasFetched(false);
				return;
			}
			setLoading(true);
			setError("");
			pythonVmInstance
				.post(
					"/discount-master/report/analytics",
					{
						start_date: startDate,
						end_date: endDate,
						brand: requestBrands,
						channel: requestChannels,
						division: requestDivisions,
						force_refresh: forceRefresh,
					},
					{ timeout: 180000 }
				)
				.then((res) => {
					setReport({ ...emptyReport, ...(res?.data || {}) });
					setHasFetched(true);
				})
				.catch((err) => {
					setError(err?.response?.data?.message || err.message || "Failed to load report");
					setReport(emptyReport);
					setHasFetched(false);
				})
				.finally(() => setLoading(false));
		},
		[startDate, endDate, requestBrands, requestChannels, requestDivisions, hasAccess]
	);

	// Initial load once brand & channel access is ready
	useEffect(() => {
		if (!hasAccess || initialLoadRef.current) return;
		initialLoadRef.current = true;
		fetchReport(false);
	}, [hasAccess, fetchReport]);

	const kpis = report?.kpis || {};
	const changes = report?.kpi_changes || {};
	const schemes = report?.schemes || {};
	const insights = report?.insights || emptyReport.insights;

	const efficientChart = useMemo(() => {
		const rows = insights.top_efficient_schemes || [];
		return {
			labels: rows.map(
				(r) => `${r.scheme_name || "Scheme"} · ${r.channel || "-"}`
			),
			values: rows.map((r) => Number(r.catalog_discount_pct) || 0),
			tooltips: rows.map((r) =>
				buildSchemeTooltip(r, "Absorbed %", formatPct(r.catalog_discount_pct))
			),
			rows,
		};
	}, [insights.top_efficient_schemes]);

	const weakChart = useMemo(() => {
		const rows = insights.weak_efficient_schemes || [];
		return {
			labels: rows.map(
				(r) => `${r.scheme_name || "Scheme"} · ${r.channel || "-"}`
			),
			values: rows.map((r) => Number(r.catalog_discount_pct) || 0),
			tooltips: rows.map((r) =>
				buildSchemeTooltip(r, "Absorbed %", formatPct(r.catalog_discount_pct))
			),
			rows,
		};
	}, [insights.weak_efficient_schemes]);

	const contributionChart = useMemo(() => {
		const rows = insights.top_sale_schemes || [];
		return {
			labels: rows.map(
				(r) => `${r.scheme_name || "Scheme"} · ${r.channel || "-"}`
			),
			values: rows.map((r) => Number(r.contribution_pct) || 0),
			tooltips: rows.map((r) =>
				buildSchemeTooltip(r, "Sale share", formatPct(r.contribution_pct))
			),
			rows,
		};
	}, [insights.top_sale_schemes]);

	const channelIntensityChart = useMemo(() => {
		const rows = insights.channel_intensity || [];
		return {
			labels: rows.map((r) => r.channel || "Unknown"),
			catalogPct: rows.map((r) => Number(r.catalog_discount_pct) || 0),
			tooltips: rows.map((r) => {
				const ch = escapeHtml(r.channel || "Unknown");
				const ofMrp = discountPctOfMrp(r);
				return `
					<div style="font-weight:700;font-size:13px;margin-bottom:6px">${ch}</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Sale</span><strong>${escapeHtml(formatINR(r.net_sale))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Discount given</span><strong>${escapeHtml(formatINR(r.absorbed_discount))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Discount % of MRP</span><strong>${escapeHtml(
							ofMrp == null ? "—" : formatPct(ofMrp)
						)}</strong>
					</div>`;
			}),
			rows,
		};
	}, [insights.channel_intensity]);

	const insightSummary = useMemo(() => {
		const scorecard = insights.top_sale_schemes || [];
		let keep = 0;
		let tune = 0;
		let cut = 0;
		scorecard.forEach((row) => {
			const action = getAbsorbedSchemeAction(row.catalog_discount_pct);
			if (action.label === "Keep & grow") keep += 1;
			else if (action.label === "Review / cut") cut += 1;
			else if (action.label === "Tune discount") tune += 1;
		});
		return {
			keep,
			tune,
			cut,
			bestCount: (insights.top_efficient_schemes || []).length,
			weakCount: (insights.weak_efficient_schemes || []).length,
			scorecardCount: scorecard.length,
		};
	}, [insights]);

	/** One chart per channel × brand with sale (skip empty pairs). */
	const channelBrandSaleCharts = useMemo(() => {
		const frames = report.scheme_frame_sales || [];
		const brandDaily = report.brand_daily || [];

		const channelOrder = ["EBO", "LFS", "MBO"];
		const selectedChannels = (requestChannels || []).map((c) => String(c).toUpperCase());
		const channels = [
			...channelOrder.filter((c) => selectedChannels.includes(c)),
			...selectedChannels.filter((c) => !channelOrder.includes(c)).sort(),
		];

		const selectedBrands = (requestBrands || []).map((b) => String(b).toUpperCase());
		const brands = [
			...ALL_BRAND_OPTIONS.filter((b) => selectedBrands.includes(b)),
			...selectedBrands.filter((b) => !ALL_BRAND_OPTIONS.includes(b)),
		];

		const saleKeys = new Set();
		brandDaily.forEach((row) => {
			const ch = String(row.channel || "").toUpperCase();
			const br = String(row.brand || "").trim().toUpperCase();
			if (!ch || !br) return;
			if ((Number(row.net_sale) || 0) > 0) {
				saleKeys.add(`${ch}||${br}`);
			}
		});

		const items = [];
		channels.forEach((channel) => {
			brands.forEach((brand) => {
				if (!saleKeys.has(`${channel}||${brand}`)) return;
				items.push({
					channel,
					brand,
					brandDaily,
					schemeFrames: frames,
					rangeStart: startDate,
					rangeEnd: endDate,
				});
			});
		});
		return items;
	}, [
		report.scheme_frame_sales,
		report.brand_daily,
		requestChannels,
		requestBrands,
		startDate,
		endDate,
	]);

	const schemeFrameChart = useMemo(() => {
		const build = (rows) => ({
			labels: rows.map((r) => `${r.scheme_name || "Scheme"} · ${r.channel || "-"}`),
			netSale: rows.map((r) => Number(r.net_sale) || 0),
			absorbed: rows.map((r) => Number(r.absorbed_discount) || 0),
			tooltips: rows.map((r) => {
				const period =
					r.frame_start && r.frame_end
						? `${moment(r.frame_start).format("DD MMM")} → ${moment(r.frame_end).format("DD MMM")}`
						: "—";
				return buildSchemeTooltip(
					{ ...r, contribution_pct: undefined },
					"Period",
					period
				);
			}),
			rows,
		});

		const rows = (report.scheme_frame_sales || [])
			.filter((r) => (Number(r.net_sale) || 0) > 0 || (Number(r.absorbed_discount) || 0) > 0)
			.slice(0, 12);
		if (!rows.length) {
			const all = (report.scheme_frame_sales || []).slice(0, 12);
			if (!all.length) return { labels: [], netSale: [], absorbed: [], tooltips: [], rows: [] };
			return build(all);
		}
		return build(rows);
	}, [report.scheme_frame_sales]);

	const overviewMonthWise = useMemo(
		() => isMonthWiseRange(startDate, endDate),
		[startDate, endDate]
	);

	const saleTrendChart = useMemo(() => {
		const dailyTrend = report.trend || [];
		if (!dailyTrend.length && !(startDate && endDate)) {
			return { labels: [], netSale: [], absorbed: [], tooltips: [], monthWise: false };
		}

		if (overviewMonthWise) {
			const monthKeys =
				startDate && endDate
					? buildContinuousMonths(startDate, endDate)
					: [
							...new Set(
								dailyTrend
									.map((r) => (r.date ? moment(r.date).format("YYYY-MM") : null))
									.filter(Boolean)
							),
					  ].sort();

			const buckets = {};
			monthKeys.forEach((m) => {
				buckets[m] = emptyMetricBucket();
			});
			dailyTrend.forEach((r) => {
				if (!r.date) return;
				const key = moment(r.date).format("YYYY-MM");
				if (!buckets[key]) buckets[key] = emptyMetricBucket();
				addMetrics(buckets[key], r);
			});

			const labels = monthKeys.map((m) => moment(m, "YYYY-MM").format("MMM YYYY"));
			const netSale = monthKeys.map((m) => metricsFromBucket(buckets[m]).net_sale);
			const absorbed = monthKeys.map(
				(m) => metricsFromBucket(buckets[m]).absorbed_discount
			);
			const tooltips = monthKeys.map((m) => {
				const metrics = metricsFromBucket(buckets[m]);
				const dateLabel = moment(m, "YYYY-MM").format("MMM YYYY");
				const ofMrp = discountPctOfMrp(metrics);
				return `
					<div style="font-weight:700;font-size:13px;margin-bottom:6px">${escapeHtml(dateLabel)}</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Net sale</span>
						<strong>${escapeHtml(formatINR(metrics.net_sale))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Discount given</span>
						<strong>${escapeHtml(formatINR(metrics.absorbed_discount))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Discount % of MRP</span>
						<strong>${escapeHtml(ofMrp == null ? "—" : formatPct(ofMrp))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Qty sold</span>
						<strong>${escapeHtml(formatNumber(metrics.qty_sold))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Bills</span>
						<strong>${escapeHtml(formatNumber(metrics.bill_count))}</strong>
					</div>`;
			});

			return { labels, netSale, absorbed, tooltips, monthWise: true };
		}

		const labels = dailyTrend.map((r) => moment(r.date).format("DD MMM"));
		const netSale = dailyTrend.map((r) => Number(r.net_sale) || 0);
		const absorbed = dailyTrend.map((r) => Number(r.absorbed_discount) || 0);
		const tooltips = dailyTrend.map((r) => {
			const dateLabel = moment(r.date).format("DD MMM YYYY");
			const ofMrp = discountPctOfMrp(r);
			return `
				<div style="font-weight:700;font-size:13px;margin-bottom:6px">${escapeHtml(dateLabel)}</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Net sale</span>
					<strong>${escapeHtml(formatINR(r.net_sale))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Discount given</span>
					<strong>${escapeHtml(formatINR(r.absorbed_discount))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Discount % of MRP</span>
					<strong>${escapeHtml(ofMrp == null ? "—" : formatPct(ofMrp))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Qty sold</span>
					<strong>${escapeHtml(formatNumber(r.qty_sold))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Bills</span>
					<strong>${escapeHtml(formatNumber(r.bill_count))}</strong>
				</div>`;
		});

		return { labels, netSale, absorbed, tooltips, monthWise: false };
	}, [report.trend, startDate, endDate, overviewMonthWise]);

	const channelBrandStack = useMemo(() => {
		const rows = report.sales_by_channel_brand || [];
		if (!rows.length) {
			return { channelCategories: [], brandSeries: [], brandCategories: [], channelSeries: [] };
		}

		const channelOrder = [];
		const brandOrder = [];
		const channelSet = new Set();
		const brandSet = new Set();
		rows.forEach((r) => {
			const ch = r.channel || "Unknown";
			const br = r.brand || "Unknown";
			if (!channelSet.has(ch)) {
				channelSet.add(ch);
				channelOrder.push(ch);
			}
			if (!brandSet.has(br)) {
				brandSet.add(br);
				brandOrder.push(br);
			}
		});

		// Cap brands in stack for readability (top by total net sale)
		const brandTotals = {};
		rows.forEach((r) => {
			const br = r.brand || "Unknown";
			brandTotals[br] = (brandTotals[br] || 0) + (Number(r.net_sale) || 0);
		});
		const topBrands = [...brandOrder]
			.sort((a, b) => (brandTotals[b] || 0) - (brandTotals[a] || 0))
			.slice(0, 8);

		const lookup = {};
		rows.forEach((r) => {
			lookup[`${r.channel || "Unknown"}||${r.brand || "Unknown"}`] = Number(r.net_sale) || 0;
		});

		const brandSeries = topBrands.map((brand) => ({
			key: brand,
			label: brand,
			values: channelOrder.map((ch) => lookup[`${ch}||${brand}`] || 0),
		}));

		const channelSeries = channelOrder.map((channel) => ({
			key: channel,
			label: channel,
			color: { EBO: PASTEL.ebo, LFS: PASTEL.lfs, MBO: PASTEL.mbo }[channel] || undefined,
			values: topBrands.map((br) => lookup[`${channel}||${br}`] || 0),
		}));

		return {
			channelCategories: channelOrder,
			brandSeries,
			brandCategories: topBrands,
			channelSeries,
		};
	}, [report.sales_by_channel_brand]);

	/** Channel & Brand tab — comparison tables + takeaways */
	const channelBrandInsights = useMemo(() => {
		const enrich = (row, nameKey) => {
			const net = Number(row.net_sale) || 0;
			const absorbed = Number(row.absorbed_discount) || 0;
			const qty = Number(row.qty_sold) || 0;
			const bills = Number(row.bill_count) || 0;
			const mrp = Number(row.mrp_sale) || 0;
			const catalogPct =
				row.catalog_discount_pct != null
					? Number(row.catalog_discount_pct)
					: mrp > 0
						? (absorbed / mrp) * 100
						: 0;
			const asp = qty > 0 ? net / qty : 0;
			const aov = bills > 0 ? net / bills : 0;
			const action = getAbsorbedSchemeAction(catalogPct);
			return {
				...row,
				name: row[nameKey] || row.channel || row.brand || "Unknown",
				net,
				absorbed,
				qty,
				bills,
				catalogPct,
				asp,
				aov,
				action,
			};
		};

		const channelSrc =
			(insights.channel_intensity || []).length > 0
				? insights.channel_intensity
				: report.sales_by_channel || [];
		const channels = channelSrc
			.map((r) => enrich(r, "channel"))
			.sort((a, b) => b.net - a.net);

		const brandSrc =
			(insights.brand_intensity || []).length > 0
				? insights.brand_intensity
				: report.sales_by_brand || [];
		const brands = brandSrc
			.map((r) => enrich(r, "brand"))
			.sort((a, b) => b.net - a.net);

		const totalNet = channels.reduce((s, r) => s + r.net, 0) || 1;
		const totalAbs = channels.reduce((s, r) => s + r.absorbed, 0);

		const channelsWithShare = channels.map((r) => ({
			...r,
			saleShare: (r.net / totalNet) * 100,
		}));
		const brandsWithShare = brands.map((r) => {
			const brandTotalNet = brands.reduce((s, x) => s + x.net, 0) || 1;
			return { ...r, saleShare: (r.net / brandTotalNet) * 100 };
		});

		const topChannel = channelsWithShare[0] || null;
		const topBrand = brandsWithShare[0] || null;
		const heaviestDiscount = [...channelsWithShare].sort(
			(a, b) => b.catalogPct - a.catalogPct
		)[0];
		const leanestDiscount = [...channelsWithShare]
			.filter((r) => r.net > 0)
			.sort((a, b) => a.catalogPct - b.catalogPct)[0];

		// Channel × brand pairs with discount (top by sale)
		const pairRows = (report.sales_by_channel_brand || [])
			.map((r) => {
				const net = Number(r.net_sale) || 0;
				const absorbed = Number(r.absorbed_discount) || 0;
				const mrp = Number(r.mrp_sale) || 0;
				const qty = Number(r.qty_sold) || 0;
				const bills = Number(r.bill_count) || 0;
				const catalogPct =
					Number(r.catalog_discount_pct) ||
					(mrp > 0 ? (absorbed / mrp) * 100 : 0);
				return {
					channel: r.channel || "Unknown",
					brand: r.brand || "Unknown",
					net,
					absorbed,
					qty,
					bills,
					asp: qty > 0 ? net / qty : 0,
					aov: bills > 0 ? net / bills : 0,
					catalogPct,
					action: getAbsorbedSchemeAction(catalogPct),
				};
			})
			.filter((r) => r.net > 0 || r.absorbed > 0)
			.sort((a, b) => b.net - a.net)
			.slice(0, 15);

		return {
			channels: channelsWithShare,
			brands: brandsWithShare,
			pairs: pairRows,
			totalNet,
			totalAbs,
			topChannel,
			topBrand,
			heaviestDiscount,
			leanestDiscount,
			channelDiscChart: {
				labels: channelsWithShare.map((r) => r.name),
				sale: channelsWithShare.map((r) => r.net),
				absorbed: channelsWithShare.map((r) => r.absorbed),
				tooltips: channelsWithShare.map(
					(r) => `
					<div style="font-weight:700;font-size:13px;margin-bottom:6px">${escapeHtml(r.name)}</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Sale</span><strong>${escapeHtml(formatINR(r.net))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Sale share</span><strong>${escapeHtml(formatPct(r.saleShare))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Discount given</span><strong>${escapeHtml(formatINR(r.absorbed))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">Discount % of MRP</span><strong>${escapeHtml(formatPct(r.catalogPct))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">ASP</span><strong>${escapeHtml(formatINR(r.asp))}</strong>
					</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
						<span style="color:#b0bec5">AOV</span><strong>${escapeHtml(formatINR(r.aov))}</strong>
					</div>
					<div style="margin-top:8px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.12);color:${r.action.color};font-weight:600">
						${escapeHtml(r.action.label)}
					</div>`
				),
			},
		};
	}, [
		insights.channel_intensity,
		insights.brand_intensity,
		report.sales_by_channel,
		report.sales_by_brand,
		report.sales_by_channel_brand,
	]);

	const storeInsights = useMemo(() => {
		const enrich = (r) => {
			const net = Number(r.net_sale) || 0;
			const absorbed = Number(r.absorbed_discount) || 0;
			const qty = Number(r.qty_sold) || 0;
			const bills = Number(r.bill_count) || 0;
			const mrp = Number(r.mrp_sale) || 0;
			const catalogPct =
				Number(r.catalog_discount_pct) || (mrp > 0 ? (absorbed / mrp) * 100 : 0);
			const asp = qty > 0 ? net / qty : 0;
			const aov = bills > 0 ? net / bills : 0;
			const action = getAbsorbedSchemeAction(catalogPct);
			return {
				store: r.store || "Unknown",
				channel: String(r.channel || "Unknown").toUpperCase(),
				net,
				absorbed,
				qty,
				bills,
				catalogPct,
				asp,
				aov,
				action,
			};
		};

		const rows = (report.top_stores || []).map(enrich);
		const totalNet = rows.reduce((s, r) => s + r.net, 0) || 1;
		const totalAbs = rows.reduce((s, r) => s + r.absorbed, 0);
		const withShare = rows.map((r) => ({
			...r,
			saleShare: (r.net / totalNet) * 100,
		}));

		const byChannelMap = {};
		withShare.forEach((r) => {
			if (!byChannelMap[r.channel]) byChannelMap[r.channel] = [];
			byChannelMap[r.channel].push(r);
		});

		const channelOrder = Object.keys(byChannelMap).sort();
		const byChannel = channelOrder.map((channel) => {
			const list = [...byChannelMap[channel]]
				.sort((a, b) => b.absorbed - a.absorbed)
				.slice(0, 5);
			const tip = (row) => `
				<div style="font-weight:700;font-size:13px;margin-bottom:6px;word-break:break-word">${escapeHtml(
					row.store
				)}</div>
				<div style="color:#90a4ae;font-size:11px;margin-bottom:4px">${escapeHtml(channel)}</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Sale</span><strong>${escapeHtml(formatINR(row.net))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Discount given</span><strong>${escapeHtml(
						formatINR(row.absorbed)
					)}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px">
					<span style="color:#b0bec5">Discount % of MRP</span><strong>${escapeHtml(
						formatPct(row.catalogPct)
					)}</strong>
				</div>
				<div style="margin-top:8px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.12);color:${
					row.action.color
				};font-weight:600">
					${escapeHtml(row.action.label)} · ${escapeHtml(row.action.hint)}
				</div>`;
			return {
				channel,
				color:
					{ EBO: PASTEL.ebo, LFS: PASTEL.lfs, MBO: PASTEL.mbo }[channel] || PASTEL.sale,
				stores: list,
				chart: {
					labels: list.map((r) => r.store),
					absorbed: list.map((r) => r.absorbed),
					sale: list.map((r) => r.net),
					tooltips: list.map(tip),
				},
			};
		});

		const flat = byChannel.flatMap((c) => c.stores);
		const topSale = [...flat].sort((a, b) => b.net - a.net)[0] || null;
		const topDiscount = [...flat].sort((a, b) => b.absorbed - a.absorbed)[0] || null;
		const heaviest =
			[...flat].filter((r) => r.net > 0).sort((a, b) => b.catalogPct - a.catalogPct)[0] ||
			null;
		const leanest =
			[...flat].filter((r) => r.net > 0).sort((a, b) => a.catalogPct - b.catalogPct)[0] ||
			null;

		return {
			rows: withShare,
			byChannel,
			totalNet,
			totalAbs,
			topSale,
			topDiscount,
			heaviest,
			leanest,
		};
	}, [report.top_stores]);

	const statusPie = useMemo(
		() =>
			(schemes.by_status || []).map((row, idx) => ({
				id: idx,
				label: row.status,
				value: Number(row.count) || 0,
				color: CHART_COLORS[idx % CHART_COLORS.length],
			})),
		[schemes.by_status]
	);

	const schemeChannelChart = useMemo(() => {
		const rows = schemes.by_channel || [];
		return {
			labels: rows.map((r) => r.channel || "Unknown"),
			values: rows.map((r) => Number(r.count) || 0),
			tooltips: rows.map((r) => {
				const ch = escapeHtml(r.channel || "Unknown");
				return `
					<div style="font-weight:700;margin-bottom:4px">${ch}</div>
					<div style="display:flex;justify-content:space-between;gap:14px">
						<span style="color:#b0bec5">Schemes</span><strong>${Number(r.count) || 0}</strong>
					</div>`;
			}),
		};
	}, [schemes.by_channel]);

	const schemePerformance = useMemo(() => {
		const frames = report.scheme_frame_sales || [];
		return [...frames]
			.map((r) => {
				const net = Number(r.net_sale) || 0;
				const absorbed = Number(r.absorbed_discount) || 0;
				const catalogPct = Number(r.catalog_discount_pct) || 0;
				const brands = Array.isArray(r.brands) ? r.brands : [];
				const period =
					r.frame_start && r.frame_end
						? `${moment(r.frame_start).format("DD MMM")} → ${moment(r.frame_end).format(
								"DD MMM"
						  )}`
						: "—";
				return {
					...r,
					net,
					absorbed,
					catalogPct,
					brands,
					brandsLabel: brands.length ? brands.join(", ") : "—",
					period,
					action: getAbsorbedSchemeAction(catalogPct),
				};
			})
			.sort((a, b) => b.net - a.net);
	}, [report.scheme_frame_sales]);

	const schemeList = useMemo(() => {
		const list = schemes.schemes?.length
			? schemes.schemes
			: schemes.recent_schemes || [];
		return list.map((r) => {
			const brands = Array.isArray(r.brands)
				? r.brands
				: Array.isArray(r.brand)
					? r.brand
					: r.brand
						? [r.brand]
						: [];
			return {
				...r,
				brandsLabel: brands.length ? brands.join(", ") : "—",
			};
		});
	}, [schemes.schemes, schemes.recent_schemes]);

	const overviewChannelCharts = useMemo(() => {
		const channelColors = {
			EBO: PASTEL.ebo,
			LFS: PASTEL.lfs,
			MBO: PASTEL.mbo,
		};
		const channelOrder = ["EBO", "LFS", "MBO"];
		const trendRows = report.trend_by_channel || [];
		const dailyTrend = report.trend || [];
		const monthWise = overviewMonthWise;

		const periodKeys = monthWise
			? startDate && endDate
				? buildContinuousMonths(startDate, endDate)
				: (() => {
						const all = [
							...new Set([
								...trendRows
									.map((r) => (r.date ? moment(r.date).format("YYYY-MM") : null))
									.filter(Boolean),
								...dailyTrend
									.map((r) => (r.date ? moment(r.date).format("YYYY-MM") : null))
									.filter(Boolean),
							]),
						].sort();
						return all;
				  })()
			: startDate && endDate
				? buildContinuousDates(startDate, endDate)
				: (() => {
						const allDates = [
							...new Set([
								...trendRows.map((r) => r.date).filter(Boolean),
								...dailyTrend.map((r) => r.date).filter(Boolean),
							]),
						].sort();
						if (!allDates.length) return [];
						return buildContinuousDates(allDates[0], allDates[allDates.length - 1]);
				  })();

		const labels = periodKeys.map((key) =>
			monthWise
				? moment(key, "YYYY-MM").format("MMM YYYY")
				: moment(key).format("DD MMM")
		);

		const periodKeyOf = (date) =>
			monthWise ? moment(date).format("YYYY-MM") : date;

		const channelsPresent = channelOrder.filter((ch) =>
			trendRows.some((r) => String(r.channel || "").toUpperCase() === ch)
		);
		const channels =
			channelsPresent.length > 0
				? channelsPresent
				: [
						...new Set(
							trendRows.map((r) => String(r.channel || "").toUpperCase()).filter(Boolean)
						),
				  ].sort();

		const saleByChannelPeriod = {};
		trendRows.forEach((r) => {
			const ch = String(r.channel || "").toUpperCase() || "Unknown";
			if (!r.date) return;
			const key = periodKeyOf(r.date);
			if (!saleByChannelPeriod[ch]) saleByChannelPeriod[ch] = {};
			saleByChannelPeriod[ch][key] =
				(saleByChannelPeriod[ch][key] || 0) + (Number(r.net_sale) || 0);
		});

		const saleSeries = channels.map((ch) => ({
			label: ch,
			color: channelColors[ch] || PASTEL.sale,
			values: periodKeys.map((key) => saleByChannelPeriod[ch]?.[key] || 0),
		}));

		const saleTooltips = periodKeys.map((key, di) => {
			const dateLabel = escapeHtml(
				monthWise
					? moment(key, "YYYY-MM").format("MMM YYYY")
					: moment(key).format("DD MMM YYYY")
			);
			const rowsHtml = channels
				.map((ch) => {
					const v = saleSeries.find((s) => s.label === ch)?.values[di] || 0;
					return `
						<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
							<span style="color:#b0bec5">${escapeHtml(ch)}</span>
							<strong>${escapeHtml(formatINR(v))}</strong>
						</div>`;
				})
				.join("");
			return `<div style="font-weight:700;margin-bottom:4px">${dateLabel}</div>${rowsHtml}`;
		});

		const metricBuckets = {};
		periodKeys.forEach((key) => {
			metricBuckets[key] = emptyMetricBucket();
		});
		dailyTrend.forEach((r) => {
			if (!r.date) return;
			const key = periodKeyOf(r.date);
			if (!metricBuckets[key]) metricBuckets[key] = emptyMetricBucket();
			addMetrics(metricBuckets[key], r);
		});

		const aspValues = periodKeys.map(
			(key) => metricsFromBucket(metricBuckets[key]).asp
		);
		const aovValues = periodKeys.map(
			(key) => metricsFromBucket(metricBuckets[key]).aov
		);

		const aovAspTooltips = periodKeys.map((key, di) => {
			const metrics = metricsFromBucket(metricBuckets[key]);
			const dateLabel = escapeHtml(
				monthWise
					? moment(key, "YYYY-MM").format("MMM YYYY")
					: moment(key).format("DD MMM YYYY")
			);
			return `
				<div style="font-weight:700;margin-bottom:4px">${dateLabel}</div>
				<div style="display:flex;justify-content:space-between;gap:14px">
					<span style="color:#b0bec5">ASP</span><strong>${escapeHtml(formatINR(aspValues[di]))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">AOV</span><strong>${escapeHtml(formatINR(aovValues[di]))}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">Net sale</span><strong>${escapeHtml(
						formatINR(metrics.net_sale)
					)}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">Qty</span><strong>${escapeHtml(
						formatNumber(metrics.qty_sold)
					)}</strong>
				</div>
				<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
					<span style="color:#b0bec5">Bills</span><strong>${escapeHtml(
						formatNumber(metrics.bill_count)
					)}</strong>
				</div>`;
		});

		return {
			labels,
			saleSeries,
			saleTooltips,
			asp: aspValues,
			aov: aovValues,
			aovAspTooltips,
			monthWise,
			hasSale: saleSeries.some((s) => s.values.some((v) => v > 0)),
			hasAovAsp: aspValues.some((v) => v > 0) || aovValues.some((v) => v > 0),
		};
	}, [
		report.trend_by_channel,
		report.trend,
		startDate,
		endDate,
		overviewMonthWise,
	]);

	const discountImpact = useMemo(() => {
		const net = Number(kpis.net_sale) || 0;
		const absorbed = Number(kpis.absorbed_discount) || 0;
		const mrp = Number(kpis.mrp_sale) || 0;
		const bills = Number(kpis.bill_count) || 0;
		// Catalog depth: absorbed ÷ MRP
		const absorbedPctOfMrp =
			kpis.catalog_discount_pct != null
				? Number(kpis.catalog_discount_pct)
				: mrp > 0
					? (absorbed / mrp) * 100
					: 0;

		const frames = report.scheme_frame_sales || [];

		let keep = 0;
		let tune = 0;
		let cut = 0;
		let monitor = 0;
		frames.forEach((row) => {
			const action = getAbsorbedSchemeAction(row.catalog_discount_pct);
			if (action.label === "Keep & grow") keep += 1;
			else if (action.label === "Review / cut") cut += 1;
			else if (action.label === "Tune discount") tune += 1;
			else monitor += 1;
		});

		const channelRows = [...(insights.channel_intensity || [])].sort(
			(a, b) =>
				(Number(b.catalog_discount_pct) || 0) - (Number(a.catalog_discount_pct) || 0)
		);
		const heaviestChannel = channelRows[0] || null;

		return {
			net,
			absorbed,
			absorbedPct: absorbedPctOfMrp,
			absorbedPctOfMrp,
			absorbedPerBill:
				kpis.absorbed_per_bill != null
					? Number(kpis.absorbed_per_bill)
					: bills > 0
						? absorbed / bills
						: 0,
			keep,
			tune,
			cut,
			monitor,
			channelChart: {
				labels: channelRows.map((r) => r.channel || "Unknown"),
				absorbedPct: channelRows.map((r) => Number(r.catalog_discount_pct) || 0),
				absorbed: channelRows.map((r) => Number(r.absorbed_discount) || 0),
				netSale: channelRows.map((r) => Number(r.net_sale) || 0),
				tooltips: channelRows.map((r) => {
					const ch = escapeHtml(r.channel || "Unknown");
					return `
						<div style="font-weight:700;margin-bottom:4px">${ch}</div>
						<div style="display:flex;justify-content:space-between;gap:14px">
							<span style="color:#b0bec5">Discount % of MRP</span>
							<strong>${escapeHtml(formatPct(r.catalog_discount_pct))}</strong>
						</div>
						<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
							<span style="color:#b0bec5">Absorbed discount</span>
							<strong>${escapeHtml(formatINR(r.absorbed_discount))}</strong>
						</div>
						<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
							<span style="color:#b0bec5">Sale</span>
							<strong>${escapeHtml(formatINR(r.net_sale))}</strong>
						</div>`;
				}),
				amountTooltips: channelRows.map((r) => {
					const ch = escapeHtml(r.channel || "Unknown");
					return `
						<div style="font-weight:700;margin-bottom:4px">${ch}</div>
						<div style="display:flex;justify-content:space-between;gap:14px">
							<span style="color:#b0bec5">Absorbed discount</span>
							<strong>${escapeHtml(formatINR(r.absorbed_discount))}</strong>
						</div>
						<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
							<span style="color:#b0bec5">Discount % of MRP</span>
							<strong>${escapeHtml(formatPct(r.catalog_discount_pct))}</strong>
						</div>
						<div style="display:flex;justify-content:space-between;gap:14px;margin-top:3px">
							<span style="color:#b0bec5">Sale</span>
							<strong>${escapeHtml(formatINR(r.net_sale))}</strong>
						</div>`;
				}),
			},
			heaviestChannel,
		};
	}, [report.kpis, insights.channel_intensity, report.scheme_frame_sales]);

	return (
		<Box
			sx={{
				bgcolor: "#f7f8fa",
				borderRadius: 2,
				p: { xs: 1.5, md: 2 },
				mt: 1,
				width: "100%",
				maxWidth: "100%",
				minWidth: 0,
				overflowX: "auto",
				overflowY: "visible",
				boxSizing: "border-box",
				"& .MuiGrid-container": {
					width: "100%",
					maxWidth: "100%",
				},
				"& .MuiGrid-item": {
					minWidth: 0,
					maxWidth: "100%",
					boxSizing: "border-box",
				},
			}}>
			{/* Filters */}
			<Paper
				elevation={0}
				sx={{
					p: 2,
					mb: 2,
					borderRadius: 2,
					border: "1px solid #e8eaed",
					overflow: "hidden",
					width: "100%",
					maxWidth: "100%",
					minWidth: 0,
					boxSizing: "border-box",
				}}>
				<Grid container spacing={1.5} alignItems="center" sx={gridContainerSx}>
					<Grid item xs={12} sm={6} md={2} sx={gridItemSx}>
						<TextField
							fullWidth
							label="From"
							type="date"
							size="small"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2} sx={gridItemSx}>
						<TextField
							fullWidth
							label="To"
							type="date"
							size="small"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2} sx={gridItemSx}>
						<MultiSelectFilter
							label="Brand"
							options={accessibleBrands}
							value={brands}
							onChange={setBrands}
							emptyLabel={`All accessible (${accessibleBrands.length})`}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2} sx={gridItemSx}>
						<MultiSelectFilter
							label="Channel"
							options={accessibleChannels}
							value={channels}
							onChange={setChannels}
							emptyLabel={`All accessible (${accessibleChannels.length})`}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2} sx={gridItemSx}>
						<MultiSelectFilter
							label="Division"
							options={ALL_DIVISION_OPTIONS}
							value={divisions}
							onChange={setDivisions}
							emptyLabel={`All (${ALL_DIVISION_OPTIONS.length})`}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2} sx={gridItemSx}>
						<Button
							fullWidth
							variant="contained"
							startIcon={
								loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
							}
							onClick={() => fetchReport(true)}
							disabled={loading || !hasAccess}
							sx={{ textTransform: "none", borderRadius: 2, height: 40 }}>
							Refresh
						</Button>
					</Grid>
				</Grid>
				<Typography
					sx={{
						mt: 1.5,
						fontSize: 12,
						color: "#78909c",
						lineHeight: 1.45,
					}}>
					Discounts considered for From–To: any <b>Published / Force Closed / Discount Closed / Closed</b>{" "}
					scheme whose live window overlaps this range (start → extended end, or close date if closed),
					for your selected brands, channels &amp; divisions. Sale charts use only the overlapping days
					inside From–To.
				</Typography>
			</Paper>

			{error ? (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			) : null}
			{report?.errors?.sales ? (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Sales analytics unavailable: {report.errors.sales}
				</Alert>
			) : null}

			{loading ? (
				<ReportLoadingSkeleton />
			) : !hasFetched ? (
				<Paper
					elevation={0}
					sx={{
						borderRadius: 2,
						border: "1px dashed #cfd8dc",
						bgcolor: "#fff",
						px: 3,
						py: 8,
						textAlign: "center",
						width: "100%",
						boxSizing: "border-box",
					}}>
					<Typography sx={{ fontSize: 15, fontWeight: 500, color: "#263238", mb: 0.75 }}>
						{hasAccess ? "Preparing report…" : "No brand/channel access"}
					</Typography>
					<Typography sx={{ fontSize: 13, color: "#78909c", maxWidth: 420, mx: "auto" }}>
						{hasAccess
							? "Loading with your assigned brands and channels."
							: "No brand/channel access assigned for Discount Report."}
					</Typography>
				</Paper>
			) : (
				<Paper
					elevation={0}
					sx={{
						borderRadius: 2,
						border: "1px solid #e8eaed",
						overflow: "hidden",
						width: "100%",
						maxWidth: "100%",
						minWidth: 0,
						position: "relative",
						boxSizing: "border-box",
					}}>
					<Tabs
						value={activeTab}
						onChange={(_, next) => setActiveTab(next)}
						variant="scrollable"
						scrollButtons="auto"
						allowScrollButtonsMobile
						sx={{
							px: 1,
							borderBottom: "1px solid #e8eaed",
							bgcolor: "#fff",
							minHeight: 48,
							position: "relative",
							zIndex: 2,
							maxWidth: "100%",
							"& .MuiTabs-scroller": { overflow: "auto !important" },
							"& .MuiTab-root": {
								textTransform: "none",
								fontWeight: 500,
								minHeight: 48,
								minWidth: "auto",
								px: 1.5,
							},
						}}>
						{REPORT_TABS.map((tab) => (
							<Tab
								key={tab.key}
								icon={tab.icon}
								iconPosition="start"
								label={tab.label}
							/>
						))}
					</Tabs>

					<Box
						px={{ xs: 1.5, md: 2 }}
						pb={2}
						sx={{
							position: "relative",
							zIndex: 1,
							width: "100%",
							maxWidth: "100%",
							minWidth: 0,
							overflow: "hidden",
							boxSizing: "border-box",
						}}>
						{/* Overview */}
						<TabPanel value={activeTab} index={0}>
							<Stack spacing={1.5}>
								<Box
									sx={{
										display: "grid",
										width: "100%",
										gap: 1,
										gridTemplateColumns: {
											xs: "1fr",
											sm: "repeat(2, minmax(0, 1fr))",
											md: "repeat(4, minmax(0, 1fr))",
											lg: "repeat(7, minmax(0, 1fr))",
										},
									}}>
									<KpiCard
										title="Net Sale"
										value={formatINR(kpis.net_sale)}
										change={changes.net_sale_pct}
										icon={<ShoppingCartIcon fontSize="small" />}
										accent={PASTEL.sale}
									/>
									<KpiCard
										title="Absorbed Discount"
										value={formatPct(
											kpis.catalog_discount_pct ??
												(kpis.mrp_sale
													? ((Number(kpis.absorbed_discount) || 0) /
															Number(kpis.mrp_sale)) *
														100
													: 0)
										)}
										subtitle={`${formatINR(kpis.absorbed_discount)} · % of MRP`}
										icon={<PercentIcon fontSize="small" />}
										accent={PASTEL.discount}
									/>
									<KpiCard
										title="Qty Sold"
										value={formatNumber(kpis.qty_sold)}
										change={changes.qty_sold_pct}
										icon={<ReceiptLongIcon fontSize="small" />}
										accent={PASTEL.teal}
									/>
									<KpiCard
										title="Bills"
										value={formatNumber(kpis.bill_count)}
										subtitle={`${formatNumber(kpis.total_schemes)} schemes in window`}
										icon={<StorefrontIcon fontSize="small" />}
										accent={PASTEL.warn}
									/>
									<KpiCard
										title="ASP"
										value={formatINR(kpis.asp)}
										subtitle="Net sale ÷ qty"
										icon={<CurrencyRupeeIcon fontSize="small" />}
										accent={PASTEL.good}
									/>
									<KpiCard
										title="AOV"
										value={formatINR(kpis.aov)}
										subtitle="Net sale ÷ bills"
										icon={<ReceiptLongIcon fontSize="small" />}
										accent={PASTEL.sale}
									/>
									<KpiCard
										title="Absorbed / Bill"
										value={formatINR(kpis.absorbed_per_bill)}
										subtitle="Discount cost per bill"
										icon={<LocalOfferIcon fontSize="small" />}
										accent={PASTEL.pink}
									/>
								</Box>

								{/* Discount impact */}
								<Paper
									elevation={0}
									sx={{
										p: 1.5,
										borderRadius: 2,
										border: "1px solid #ffe0b2",
										bgcolor: "#fffaf3",
									}}>
									<Typography fontWeight={500} color={PASTEL.warn} mb={0.35}>
										Discount impact
									</Typography>
									<Typography variant="body2" color="text.secondary" mb={1.25}>
										Cost of discount for the selected period, and channel pressure.
									</Typography>

									<Box
										sx={{
											display: "grid",
											width: "100%",
											gap: 1,
											mb: 1.25,
											gridTemplateColumns: {
												xs: "1fr",
												sm: "minmax(0, 1fr)",
												md: "minmax(0, 420px)",
											},
										}}>
										<Paper
											elevation={0}
											sx={{
												p: 1.25,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												bgcolor: "#fff",
											}}>
											<Typography
												variant="caption"
												fontWeight={500}
												color={PASTEL.discount}
												display="block">
												Cost impact
											</Typography>
											<Typography
												sx={{ fontSize: 20, fontWeight: 500, color: "#455a64", mt: 0.25 }}>
												{formatINR(discountImpact.absorbed)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{formatPct(discountImpact.absorbedPctOfMrp ?? 0)} of MRP
											</Typography>
										</Paper>
									</Box>

									<Grid container spacing={1.5}>
										<Grid item xs={12}>
											<ChartCard
												title="Channel discount pressure"
												subtitle="Absorbed discount % of MRP (left) and absorbed discount ₹ (right) by channel."
												chartHeight={280}>
												{discountImpact.channelChart.labels.length ? (
													<Grid container spacing={1.5}>
														<Grid item xs={12} md={6}>
															<Typography
																variant="caption"
																color="text.secondary"
																fontWeight={500}
																sx={{ px: 0.5, display: "block", mb: 0.5 }}>
																Absorbed discount % (of MRP)
															</Typography>
															<D3BarChart
																height={240}
																layout="horizontal"
																labels={discountImpact.channelChart.labels}
																tooltips={discountImpact.channelChart.tooltips}
																summaryMode="avg"
																series={[
																	{
																		label: "Absorbed discount %",
																		color: PASTEL.discount,
																		values:
																			discountImpact.channelChart.absorbedPct,
																	},
																]}
																valueFormatter={(v) => formatPct(v)}
															/>
														</Grid>
														<Grid item xs={12} md={6}>
															<Typography
																variant="caption"
																color="text.secondary"
																fontWeight={500}
																sx={{ px: 0.5, display: "block", mb: 0.5 }}>
																Absorbed discount (₹)
															</Typography>
															<D3BarChart
																height={240}
																layout="horizontal"
																labels={discountImpact.channelChart.labels}
																tooltips={
																	discountImpact.channelChart.amountTooltips
																}
																series={[
																	{
																		label: "Absorbed discount",
																		color: PASTEL.discount,
																		values:
																			discountImpact.channelChart.absorbed,
																	},
																]}
																valueFormatter={formatINR}
															/>
														</Grid>
													</Grid>
												) : (
													<EmptyChart message="No channel discount intensity" />
												)}
											</ChartCard>
										</Grid>
									</Grid>
								</Paper>

								<Grid container spacing={1.5}>
									<Grid item xs={12} lg={8}>
										<ChartCard
											title={
												saleTrendChart.monthWise
													? "Monthly net sale vs absorbed discount"
													: "Daily net sale vs absorbed discount"
											}
											subtitle={
												saleTrendChart.monthWise
													? "Month-wise totals (range longer than 3 months). Hover: Discount % of MRP = absorbed ÷ MRP."
													: "Hover: Discount % of MRP = absorbed ÷ MRP."
											}
											chartHeight={280}>
											{saleTrendChart.labels.length ? (
												<D3LineChart
													height={280}
													labels={saleTrendChart.labels}
													tooltips={saleTrendChart.tooltips}
													series={[
														{
															label: "Net Sale",
															color: PASTEL.sale,
															values: saleTrendChart.netSale,
														},
														{
															label: "Absorbed Discount",
															color: PASTEL.discount,
															values: saleTrendChart.absorbed,
														},
													]}
													valueFormatter={formatINR}
												/>
											) : (
												<EmptyChart message="No sales trend data for selected filters" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} lg={4}>
										<ChartCard
											title="Scheme status"
											subtitle="Share of schemes in the selected window."
											chartHeight={280}>
											{statusPie.length ? (
												<D3DonutChart
													height={280}
													data={statusPie}
													valueFormatter={(v) => String(v)}
												/>
											) : (
												<EmptyChart message="No scheme status data" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} md={6}>
										<ChartCard
											title={
												overviewChannelCharts.monthWise
													? "Monthly sales by channel"
													: "Daily sales by channel"
											}
											subtitle={
												overviewChannelCharts.monthWise
													? "Month-wise net sale for EBO / LFS / MBO (range > 3 months)."
													: "Day-wise net sale for EBO / LFS / MBO."
											}
											chartHeight={280}>
											{overviewChannelCharts.hasSale ? (
												<D3LineChart
													height={260}
													labels={overviewChannelCharts.labels}
													tooltips={overviewChannelCharts.saleTooltips}
													series={overviewChannelCharts.saleSeries}
													valueFormatter={formatINR}
												/>
											) : (
												<EmptyChart message="No channel sales for selected filters" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} md={6}>
										<ChartCard
											title={
												overviewChannelCharts.monthWise
													? "Monthly AOV & ASP"
													: "Daily AOV & ASP"
											}
											subtitle={
												overviewChannelCharts.monthWise
													? "Month-wise average order value and selling price (range > 3 months)."
													: "Day-wise average order value and average selling price."
											}
											chartHeight={280}>
											{overviewChannelCharts.hasAovAsp ? (
												<D3LineChart
													height={260}
													labels={overviewChannelCharts.labels}
													tooltips={overviewChannelCharts.aovAspTooltips}
													series={[
														{
															label: "ASP",
															color: PASTEL.good,
															values: overviewChannelCharts.asp,
														},
														{
															label: "AOV",
															color: PASTEL.sale,
															values: overviewChannelCharts.aov,
														},
													]}
													valueFormatter={formatINR}
												/>
											) : (
												<EmptyChart message="No AOV / ASP data for selected filters" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12}>
										<Typography variant="subtitle1" fontWeight={500} mb={0.35} color="#263238">
											Net sale by channel × brand
										</Typography>
										<Typography variant="body2" color="#78909c" display="block" mb={1.75} sx={{ fontSize: 13 }}>
											One graph per channel + brand (e.g. EBO · RARE RABBIT). Sale line always shows.
											Colored discount bands appear only when a scheme exists for that exact channel +
											brand; otherwise only the sale line is shown.
										</Typography>
										{channelBrandSaleCharts.length ? (
											<Grid container spacing={1.5}>
												{channelBrandSaleCharts.map((item, idx) => {
													const aloneInRow =
														channelBrandSaleCharts.length === 1 ||
														(channelBrandSaleCharts.length % 2 === 1 &&
															idx === channelBrandSaleCharts.length - 1);
													return (
														<Grid
															item
															xs={12}
															md={aloneInRow ? 12 : 6}
															key={`sale-${item.channel}-${item.brand}`}>
															<ChannelBrandSaleLineChart
																channel={item.channel}
																brand={item.brand}
																brandDaily={item.brandDaily}
																schemeFrames={item.schemeFrames}
																rangeStart={item.rangeStart}
																rangeEnd={item.rangeEnd}
															/>
														</Grid>
													);
												})}
											</Grid>
										) : (
											<ChartCard title="Net sale by channel × brand" chartHeight={220}>
												<EmptyChart message="No sale data for selected channel × brand" />
											</ChartCard>
										)}
									</Grid>
									<Grid item xs={12}>
										<Typography variant="subtitle1" fontWeight={500} mb={0.35} color="#263238">
											ROS · AOV · ASP by channel × brand
										</Typography>
										<Typography
											variant="body2"
											color="#78909c"
											display="block"
											mb={1.75}
											sx={{ fontSize: 13 }}>
											Same pairs as net sale. Discount colors only when a scheme exists for that channel +
											brand; otherwise only the metric lines.
										</Typography>
										{channelBrandSaleCharts.length ? (
											<Grid container spacing={1.5}>
												{channelBrandSaleCharts.map((item, idx) => {
													const aloneInRow =
														channelBrandSaleCharts.length === 1 ||
														(channelBrandSaleCharts.length % 2 === 1 &&
															idx === channelBrandSaleCharts.length - 1);
													return (
														<Grid
															item
															xs={12}
															md={aloneInRow ? 12 : 6}
															key={`ros-${item.channel}-${item.brand}`}>
															<ChannelBrandRosAovAspLineChart
																channel={item.channel}
																brand={item.brand}
																brandDaily={item.brandDaily}
																schemeFrames={item.schemeFrames}
																rangeStart={item.rangeStart}
																rangeEnd={item.rangeEnd}
															/>
														</Grid>
													);
												})}
											</Grid>
										) : (
											<ChartCard title="ROS · AOV · ASP by channel × brand" chartHeight={220}>
												<EmptyChart message="No ROS / AOV / ASP data for selected channel × brand" />
											</ChartCard>
										)}
									</Grid>
									{schemeFrameChart.rows.length > 0 ? (
										<Grid item xs={12}>
											<Paper
												elevation={0}
												sx={{
													p: 2,
													borderRadius: 2,
													border: "1px solid #e8eaed",
													overflow: "hidden",
												}}>
												<Typography variant="subtitle1" fontWeight={500} mb={1}>
													Discount scheme details
												</Typography>
												<TableContainer sx={{ maxHeight: 320, width: "100%" }}>
													<Table size="small" stickyHeader>
														<TableHead>
															<TableRow>
																<TableCell>Scheme</TableCell>
																<TableCell>Channel</TableCell>
																<TableCell>Start</TableCell>
																<TableCell>End</TableCell>
																<TableCell>Period</TableCell>
																<TableCell align="right">Net Sale</TableCell>
																<TableCell align="right">Absorbed</TableCell>
																<TableCell align="right">ROS</TableCell>
																<TableCell align="right">ASP</TableCell>
																<TableCell align="right">AOV</TableCell>
																<TableCell align="right">Disc % of MRP</TableCell>
																<TableCell align="right">Sale / Day</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{schemeFrameChart.rows.map((row) => {
																const days = Math.max(Number(row.frame_days) || 0, 1);
																const ros =
																	row.ros != null
																		? Number(row.ros)
																		: (Number(row.qty_sold) || 0) / days;
																const asp =
																	row.asp != null
																		? Number(row.asp)
																		: Number(row.qty_sold) > 0
																			? (Number(row.net_sale) || 0) /
																			  Number(row.qty_sold)
																			: 0;
																const aov =
																	row.aov != null
																		? Number(row.aov)
																		: Number(row.bill_count) > 0
																			? (Number(row.net_sale) || 0) /
																			  Number(row.bill_count)
																			: 0;
																return (
																<TableRow key={row.scheme_id}>
																	<TableCell
																		sx={{
																			maxWidth: 180,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.scheme_name}>
																		{row.scheme_name}
																	</TableCell>
																	<TableCell>{row.channel}</TableCell>
																	<TableCell>
																		{row.start_date
																			? moment(row.start_date).format("DD MMM YYYY")
																			: "—"}
																	</TableCell>
																	<TableCell>
																		{row.end_date
																			? moment(row.end_date).format("DD MMM YYYY")
																			: "—"}
																	</TableCell>
																	<TableCell>
																		{row.frame_start && row.frame_end
																			? `${moment(row.frame_start).format("DD MMM")} → ${moment(
																					row.frame_end
																			  ).format("DD MMM")}`
																			: "—"}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.net_sale)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.absorbed_discount)}
																	</TableCell>
																	<TableCell align="right">
																		{formatNumber(ros)}
																	</TableCell>
																	<TableCell align="right">{formatINR(asp)}</TableCell>
																	<TableCell align="right">{formatINR(aov)}</TableCell>
																	<TableCell align="right">
																		{formatPct(row.catalog_discount_pct)}
																	</TableCell>
																	<TableCell align="right">
																		{row.sale_per_day != null
																			? formatINR(row.sale_per_day)
																			: "—"}
																	</TableCell>
																</TableRow>
																);
															})}
														</TableBody>
													</Table>
												</TableContainer>
											</Paper>
										</Grid>
									) : null}
								</Grid>
							</Stack>
						</TabPanel>

						{/* Decide tab hidden — restore by adding it back to REPORT_TABS and setting index={1} */}
						<TabPanel value={activeTab} index={-1}>
							<Stack spacing={1.5}>
								<Paper
									elevation={0}
									sx={{
										p: 2,
										borderRadius: 2,
										border: "1px solid #e3f2fd",
										bgcolor: "#f5faff",
									}}>
									<Typography fontWeight={500} color={PASTEL.sale} mb={0.5}>
										What should I do with my discounts?
									</Typography>
									<Typography variant="body2" color="text.secondary" mb={1}>
										This page ranks schemes by <b>absorbed discount %</b> (discount ÷ MRP).
										Lower absorbed % is better. Use the colours below to decide quickly.
									</Typography>
									<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
										{[
											{
												label: "Keep & grow",
												color: PASTEL.good,
												desc: "Absorbed % ≤ 12.5%",
											},
											{
												label: "Tune discount",
												color: PASTEL.warn,
												desc: "Absorbed % 12.5% – 33%",
											},
											{
												label: "Review / cut",
												color: PASTEL.discount,
												desc: "Absorbed % ≥ 33%",
											},
										].map((item) => (
											<Chip
												key={item.label}
												size="small"
												label={`${item.label} · ${item.desc}`}
												sx={{
													height: 26,
													fontSize: 12,
													fontWeight: 500,
													bgcolor: `${item.color}14`,
													color: item.color,
												}}
											/>
										))}
									</Stack>
								</Paper>

								<Box
									sx={{
										display: "grid",
										width: "100%",
										gap: 1,
										gridTemplateColumns: {
											xs: "1fr",
											sm: "repeat(3, minmax(0, 1fr))",
										},
									}}>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #c8e6c9",
											bgcolor: "#e8f5e9",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.good}>
											Looking healthy
										</Typography>
										<Typography sx={{ fontSize: 22, fontWeight: 500 }} color="#1b5e20">
											{insightSummary.keep}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											top schemes to keep / extend
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #ffe0b2",
											bgcolor: "#fff3e0",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.warn}>
											Needs tuning
										</Typography>
										<Typography sx={{ fontSize: 22, fontWeight: 500 }} color="#e65100">
											{insightSummary.tune}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											schemes to adjust depth or days
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #ffcdd2",
											bgcolor: "#ffebee",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.discount}>
											Needs attention
										</Typography>
										<Typography sx={{ fontSize: 22, fontWeight: 500 }} color="#b71c1c">
											{insightSummary.cut}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											schemes costing too much discount
										</Typography>
									</Paper>
								</Box>

								<Grid container spacing={1.5}>
									<Grid item xs={12} lg={6}>
										<ChartCard
											title="Best value schemes"
											subtitle="Absorbed discount ≤ 12.5% of MRP — good candidates to keep or extend."
											chartHeight={Math.max(280, efficientChart.labels.length * 42 + 60)}>
											{efficientChart.labels.length ? (
												<D3BarChart
													height={Math.max(260, efficientChart.labels.length * 42 + 60)}
													layout="horizontal"
													labels={efficientChart.labels}
													labelMax={22}
													tooltips={efficientChart.tooltips}
													summaryMode="avg"
													series={[
														{
															label: "Absorbed %",
															color: PASTEL.good,
															values: efficientChart.values,
														},
													]}
													valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
												/>
											) : (
												<EmptyChart message="No low absorbed-discount schemes (≤ 12.5% of MRP) in this period" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} lg={6}>
										<ChartCard
											title="Schemes with deep absorbed discount"
											subtitle="Absorbed discount ≥ 33% of MRP — review depth, days, or stop these."
											chartHeight={Math.max(280, weakChart.labels.length * 42 + 60)}>
											{weakChart.labels.length ? (
												<D3BarChart
													height={Math.max(260, weakChart.labels.length * 42 + 60)}
													layout="horizontal"
													labels={weakChart.labels}
													labelMax={22}
													tooltips={weakChart.tooltips}
													summaryMode="avg"
													series={[
														{
															label: "Absorbed %",
															color: PASTEL.discount,
															values: weakChart.values,
														},
													]}
													valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
												/>
											) : (
												<EmptyChart message="No deep absorbed-discount schemes (≥ 33% of MRP) in this period" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} lg={6}>
										<ChartCard
											title="Which schemes drive the most sale?"
											subtitle="Share of sale among schemes active in this period."
											chartHeight={Math.max(280, contributionChart.labels.length * 42 + 60)}>
											{contributionChart.labels.length ? (
												<D3BarChart
													height={Math.max(260, contributionChart.labels.length * 42 + 60)}
													layout="horizontal"
													labels={contributionChart.labels}
													labelMax={22}
													tooltips={contributionChart.tooltips}
													series={[
														{
															label: "Sale share",
															color: PASTEL.sale,
															values: contributionChart.values,
														},
													]}
													valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
												/>
											) : (
												<EmptyChart message="No scheme sale share data for selected filters" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} lg={6}>
										<ChartCard
											title="Absorbed discount by channel"
											subtitle="Absorbed discount % of MRP by channel."
											chartHeight={Math.max(
												280,
												channelIntensityChart.labels.length * 48 + 80
											)}>
											{channelIntensityChart.labels.length ? (
												<D3BarChart
													height={Math.max(
														260,
														channelIntensityChart.labels.length * 48 + 80
													)}
													layout="horizontal"
													labels={channelIntensityChart.labels}
													labelMax={14}
													minBandSize={48}
													tooltips={channelIntensityChart.tooltips}
													summaryMode="avg"
													series={[
														{
															label: "Absorbed % of MRP",
															color: PASTEL.discount,
															values: channelIntensityChart.catalogPct,
														},
													]}
													valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
												/>
											) : (
												<EmptyChart message="No channel absorbed discount data" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12}>
										<Paper
											elevation={0}
											sx={{
												p: 2,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												overflow: "hidden",
											}}>
											<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
												Scheme action list
											</Typography>
											<Typography variant="body2" color="text.secondary" mb={1}>
												Top schemes by sale. Recommendation uses absorbed discount % of MRP.
											</Typography>
											<TableContainer sx={{ maxHeight: 400, width: "100%" }}>
												<Table size="small" stickyHeader>
													<TableHead>
														<TableRow>
															<TableCell>Scheme</TableCell>
															<TableCell>Channel</TableCell>
															<TableCell align="right">Sale</TableCell>
															<TableCell align="right">Discount given</TableCell>
															<TableCell align="right">Absorbed %</TableCell>
															<TableCell align="right">Sale / day</TableCell>
															<TableCell align="right">Sale share</TableCell>
															<TableCell>Recommendation</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{(insights.top_sale_schemes || []).length ? (
															(insights.top_sale_schemes || []).map((row) => {
																const action = getAbsorbedSchemeAction(
																	row.catalog_discount_pct
																);
																return (
																	<TableRow key={`${row.scheme_id}-${row.channel}`}>
																		<TableCell
																			sx={{
																				maxWidth: 200,
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																				whiteSpace: "nowrap",
																			}}
																			title={row.scheme_name}>
																			{row.scheme_name}
																		</TableCell>
																		<TableCell>{row.channel}</TableCell>
																		<TableCell align="right">
																			{formatINR(row.net_sale)}
																		</TableCell>
																		<TableCell align="right">
																			{formatINR(row.absorbed_discount)}
																		</TableCell>
																		<TableCell align="right">
																			{formatPct(row.catalog_discount_pct)}
																		</TableCell>
																		<TableCell align="right">
																			{row.sale_per_day != null
																				? formatINR(row.sale_per_day)
																				: "—"}
																		</TableCell>
																		<TableCell align="right">
																			{formatPct(row.contribution_pct)}
																		</TableCell>
																		<TableCell>
																			<Chip
																				size="small"
																				label={action.label}
																				title={action.hint}
																				sx={{
																					height: 22,
																					fontSize: 11,
																					bgcolor: `${action.color}14`,
																					color: action.color,
																					fontWeight: 500,
																				}}
																			/>
																		</TableCell>
																	</TableRow>
																);
															})
														) : (
															<TableRow>
																<TableCell colSpan={9} align="center">
																	No schemes to recommend for the selected filters
																</TableCell>
															</TableRow>
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									</Grid>
									{(insights.brand_intensity || []).length > 0 ? (
										<Grid item xs={12}>
											<Paper
												elevation={0}
												sx={{
													p: 2,
													borderRadius: 2,
													border: "1px solid #e8eaed",
													overflow: "hidden",
												}}>
												<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
													Brand absorbed discount snapshot
												</Typography>
												<Typography variant="body2" color="text.secondary" mb={1}>
													Brands ranked by discount % of MRP (absorbed ÷ MRP).
												</Typography>
												<TableContainer sx={{ maxHeight: 320, width: "100%" }}>
													<Table size="small" stickyHeader>
														<TableHead>
															<TableRow>
																<TableCell>Brand</TableCell>
																<TableCell align="right">Sale</TableCell>
																<TableCell align="right">Discount given</TableCell>
																<TableCell align="right">Discount % of MRP</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{(insights.brand_intensity || []).map((row) => {
																const ofMrp = discountPctOfMrp(row);
																return (
																	<TableRow key={row.brand}>
																		<TableCell>{row.brand}</TableCell>
																		<TableCell align="right">
																			{formatINR(row.net_sale)}
																		</TableCell>
																		<TableCell align="right">
																			{formatINR(row.absorbed_discount)}
																		</TableCell>
																		<TableCell align="right">
																			{ofMrp == null ? "—" : formatPct(ofMrp)}
																		</TableCell>
																	</TableRow>
																);
															})}
														</TableBody>
													</Table>
												</TableContainer>
											</Paper>
										</Grid>
									) : null}
								</Grid>
							</Stack>
						</TabPanel>

						{/* Channel & Brand */}
						<TabPanel value={activeTab} index={1}>
							<Stack spacing={1.5}>
								<Paper
									elevation={0}
									sx={{
										p: 2,
										borderRadius: 2,
										border: "1px solid #e8f5e9",
										bgcolor: "#f1f8f4",
									}}>
									<Typography fontWeight={500} color={PASTEL.good} mb={0.5}>
										Where is sale vs discount coming from?
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Compare channels and brands by sale, discount cost, ASP and AOV. Use this
										to see which channel/brand is healthy and which is burning discount.
									</Typography>
								</Paper>

								<Box
									sx={{
										display: "grid",
										width: "100%",
										gap: 1,
										gridTemplateColumns: {
											xs: "1fr",
											sm: "repeat(2, minmax(0, 1fr))",
											md: "repeat(4, minmax(0, 1fr))",
										},
									}}>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #e8eaed",
											bgcolor: "#fff",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.sale}>
											Top channel by sale
										</Typography>
										<Typography sx={{ fontSize: 16, fontWeight: 500 }} noWrap title={channelBrandInsights.topChannel?.name}>
											{channelBrandInsights.topChannel?.name || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{channelBrandInsights.topChannel
												? `${formatINR(channelBrandInsights.topChannel.net)} · ${formatPct(
														channelBrandInsights.topChannel.saleShare
													)} share`
												: "No channel data"}
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #e8eaed",
											bgcolor: "#fff",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.purple}>
											Top brand by sale
										</Typography>
										<Typography sx={{ fontSize: 16, fontWeight: 500 }} noWrap title={channelBrandInsights.topBrand?.name}>
											{channelBrandInsights.topBrand?.name || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{channelBrandInsights.topBrand
												? `${formatINR(channelBrandInsights.topBrand.net)} · ${formatPct(
														channelBrandInsights.topBrand.saleShare
													)} share`
												: "No brand data"}
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #ffcdd2",
											bgcolor: "#fff8f8",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.discount}>
											Highest discount pressure
										</Typography>
										<Typography sx={{ fontSize: 16, fontWeight: 500 }} noWrap title={channelBrandInsights.heaviestDiscount?.name}>
											{channelBrandInsights.heaviestDiscount?.name || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{channelBrandInsights.heaviestDiscount
												? `${formatPct(
														channelBrandInsights.heaviestDiscount.catalogPct
													)} of MRP · ${formatINR(
														channelBrandInsights.heaviestDiscount.absorbed
													)}`
												: "—"}
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #c8e6c9",
											bgcolor: "#f1f8f4",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.good}>
											Leanest discount channel
										</Typography>
										<Typography sx={{ fontSize: 16, fontWeight: 500 }} noWrap title={channelBrandInsights.leanestDiscount?.name}>
											{channelBrandInsights.leanestDiscount?.name || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{channelBrandInsights.leanestDiscount
												? `Only ${formatPct(
														channelBrandInsights.leanestDiscount.catalogPct
													)} of MRP as discount`
												: "—"}
										</Typography>
									</Paper>
								</Box>

								<Grid container spacing={1.5}>
									<Grid item xs={12} lg={6}>
										<ChartCard
											title="Channel sale vs discount"
											subtitle="Compare how much each channel sells vs how much discount it takes."
											chartHeight={Math.max(
												280,
												channelBrandInsights.channelDiscChart.labels.length * 52 + 80
											)}>
											{channelBrandInsights.channelDiscChart.labels.length ? (
												<D3BarChart
													height={Math.max(
														260,
														channelBrandInsights.channelDiscChart.labels.length * 52 + 80
													)}
													layout="horizontal"
													labels={channelBrandInsights.channelDiscChart.labels}
													labelMax={10}
													minBandSize={52}
													tooltips={channelBrandInsights.channelDiscChart.tooltips}
													series={[
														{
															label: "Sale",
															color: PASTEL.sale,
															values: channelBrandInsights.channelDiscChart.sale,
														},
														{
															label: "Discount given",
															color: PASTEL.discount,
															values: channelBrandInsights.channelDiscChart.absorbed,
														},
													]}
													valueFormatter={formatINR}
												/>
											) : (
												<EmptyChart message="No channel sale / discount data" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} lg={6}>
										<ChartCard
											title="Channel contribution by brand"
											subtitle="Each bar is a channel; colours show which brands drive that sale."
											chartHeight={340}>
											{channelBrandStack.channelCategories.length ? (
												<D3StackedBarChart
													height={320}
													categories={channelBrandStack.channelCategories}
													series={channelBrandStack.brandSeries}
													valueFormatter={formatINR}
												/>
											) : (
												<EmptyChart message="No channel × brand contribution data" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12}>
										<ChartCard
											title="Brand contribution by channel"
											subtitle="Each bar is a brand; colours show EBO / LFS / MBO share."
											chartHeight={Math.max(
												320,
												channelBrandStack.brandCategories.length * 42
											)}>
											{channelBrandStack.brandCategories.length ? (
												<D3StackedBarChart
													height={Math.max(
														300,
														channelBrandStack.brandCategories.length * 42
													)}
													layout="horizontal"
													categories={channelBrandStack.brandCategories}
													series={channelBrandStack.channelSeries}
													labelMax={16}
													valueFormatter={formatINR}
												/>
											) : (
												<EmptyChart message="No brand × channel contribution data" />
											)}
										</ChartCard>
									</Grid>

									<Grid item xs={12} lg={6}>
										<Paper
											elevation={0}
											sx={{
												p: 2,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												overflow: "hidden",
												height: "100%",
											}}>
											<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
												Channel scorecard
											</Typography>
											<Typography variant="body2" color="text.secondary" mb={1}>
												Sale share, discount cost, ASP/AOV and a quick recommendation.
											</Typography>
											<TableContainer sx={{ maxHeight: 360, width: "100%" }}>
												<Table size="small" stickyHeader>
													<TableHead>
														<TableRow>
															<TableCell>Channel</TableCell>
															<TableCell align="right">Sale</TableCell>
															<TableCell align="right">Share</TableCell>
															<TableCell align="right">Discount</TableCell>
															<TableCell align="right">Disc % of MRP</TableCell>
															<TableCell align="right">ASP</TableCell>
															<TableCell align="right">AOV</TableCell>
															<TableCell>Hint</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{channelBrandInsights.channels.length ? (
															channelBrandInsights.channels.map((row) => (
																<TableRow key={row.name}>
																	<TableCell>{row.name}</TableCell>
																	<TableCell align="right">
																		{formatINR(row.net)}
																	</TableCell>
																	<TableCell align="right">
																		{formatPct(row.saleShare)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.absorbed)}
																	</TableCell>
																	<TableCell align="right">
																		{formatPct(row.catalogPct)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.asp)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.aov)}
																	</TableCell>
																	<TableCell>
																		<Chip
																			size="small"
																			label={row.action.label}
																			title={row.action.hint}
																			sx={{
																				height: 22,
																				fontSize: 11,
																				fontWeight: 500,
																				bgcolor: `${row.action.color}14`,
																				color: row.action.color,
																			}}
																		/>
																	</TableCell>
																</TableRow>
															))
														) : (
															<TableRow>
																<TableCell colSpan={8} align="center">
																	No channel data
																</TableCell>
															</TableRow>
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									</Grid>

									<Grid item xs={12} lg={6}>
										<Paper
											elevation={0}
											sx={{
												p: 2,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												overflow: "hidden",
												height: "100%",
											}}>
											<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
												Brand scorecard
											</Typography>
											<Typography variant="body2" color="text.secondary" mb={1}>
												Which brands drive sale and which take more discount.
											</Typography>
											<TableContainer sx={{ maxHeight: 360, width: "100%" }}>
												<Table size="small" stickyHeader>
													<TableHead>
														<TableRow>
															<TableCell>Brand</TableCell>
															<TableCell align="right">Sale</TableCell>
															<TableCell align="right">Share</TableCell>
															<TableCell align="right">Discount</TableCell>
															<TableCell align="right">Disc % of MRP</TableCell>
															<TableCell>Hint</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{channelBrandInsights.brands.length ? (
															channelBrandInsights.brands.map((row) => (
																<TableRow key={row.name}>
																	<TableCell
																		sx={{
																			maxWidth: 140,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.name}>
																		{row.name}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.net)}
																	</TableCell>
																	<TableCell align="right">
																		{formatPct(row.saleShare)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.absorbed)}
																	</TableCell>
																	<TableCell align="right">
																		{formatPct(row.catalogPct)}
																	</TableCell>
																	<TableCell>
																		<Chip
																			size="small"
																			label={row.action.label}
																			title={row.action.hint}
																			sx={{
																				height: 22,
																				fontSize: 11,
																				fontWeight: 500,
																				bgcolor: `${row.action.color}14`,
																				color: row.action.color,
																			}}
																		/>
																	</TableCell>
																</TableRow>
															))
														) : (
															<TableRow>
																<TableCell colSpan={6} align="center">
																	No brand data
																</TableCell>
															</TableRow>
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									</Grid>

									<Grid item xs={12}>
										<Paper
											elevation={0}
											sx={{
												p: 2,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												overflow: "hidden",
											}}>
											<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
												Channel × Brand detail
											</Typography>
											<Typography variant="body2" color="text.secondary" mb={1}>
												Top combinations by sale — useful to spot a brand that is fine overall
												but weak on one channel (or the opposite).
											</Typography>
											<TableContainer sx={{ maxHeight: 400, width: "100%" }}>
												<Table size="small" stickyHeader>
													<TableHead>
														<TableRow>
															<TableCell>Channel</TableCell>
															<TableCell>Brand</TableCell>
															<TableCell align="right">Sale</TableCell>
															<TableCell align="right">Discount</TableCell>
															<TableCell align="right">Disc % of MRP</TableCell>
															<TableCell align="right">ASP</TableCell>
															<TableCell align="right">AOV</TableCell>
															<TableCell align="right">Qty</TableCell>
															<TableCell>Hint</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{channelBrandInsights.pairs.length ? (
															channelBrandInsights.pairs.map((row) => (
																<TableRow
																	key={`${row.channel}-${row.brand}`}>
																	<TableCell>{row.channel}</TableCell>
																	<TableCell
																		sx={{
																			maxWidth: 160,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.brand}>
																		{row.brand}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.net)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.absorbed)}
																	</TableCell>
																	<TableCell align="right">
																		{formatPct(row.catalogPct)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.asp)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.aov)}
																	</TableCell>
																	<TableCell align="right">
																		{formatNumber(row.qty)}
																	</TableCell>
																	<TableCell>
																		<Chip
																			size="small"
																			label={row.action.label}
																			title={row.action.hint}
																			sx={{
																				height: 22,
																				fontSize: 11,
																				fontWeight: 500,
																				bgcolor: `${row.action.color}14`,
																				color: row.action.color,
																			}}
																		/>
																	</TableCell>
																</TableRow>
															))
														) : (
															<TableRow>
																<TableCell colSpan={10} align="center">
																	No channel × brand pairs for selected filters
																</TableCell>
															</TableRow>
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									</Grid>
								</Grid>
							</Stack>
						</TabPanel>

						{/* Stores */}
						<TabPanel value={activeTab} index={2}>
							<Stack spacing={1.5}>
								<Paper
									elevation={0}
									sx={{
										p: 1.5,
										borderRadius: 2,
										border: "1px solid #e3f2fd",
										bgcolor: "#f5faff",
									}}>
									<Typography fontWeight={500} color={PASTEL.sale} mb={0.5}>
										Top 5 stores by channel
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Each channel shows its top 5 stores by discount given, with sale, discount %,
										ASP/AOV and a Keep / Tune / Review hint.
									</Typography>
								</Paper>

								<Box
									sx={{
										display: "grid",
										width: "100%",
										gap: 1,
										gridTemplateColumns: {
											xs: "1fr",
											sm: "repeat(2, minmax(0, 1fr))",
											md: "repeat(4, minmax(0, 1fr))",
										},
									}}>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #e8eaed",
											bgcolor: "#fff",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.sale}>
											Top store by sale
										</Typography>
										<Typography
											sx={{ fontSize: 15, fontWeight: 500 }}
											noWrap
											title={
												storeInsights.topSale
													? `${storeInsights.topSale.store} (${storeInsights.topSale.channel})`
													: ""
											}>
											{storeInsights.topSale?.store || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{storeInsights.topSale
												? `${storeInsights.topSale.channel} · ${formatINR(
														storeInsights.topSale.net
													)}`
												: "No store data"}
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #ffcdd2",
											bgcolor: "#fff8f8",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.discount}>
											Highest discount ₹
										</Typography>
										<Typography
											sx={{ fontSize: 15, fontWeight: 500 }}
											noWrap
											title={
												storeInsights.topDiscount
													? `${storeInsights.topDiscount.store} (${storeInsights.topDiscount.channel})`
													: ""
											}>
											{storeInsights.topDiscount?.store || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{storeInsights.topDiscount
												? `${storeInsights.topDiscount.channel} · ${formatINR(
														storeInsights.topDiscount.absorbed
													)}`
												: "—"}
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #ffe0b2",
											bgcolor: "#fffaf3",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.warn}>
											Highest discount pressure
										</Typography>
										<Typography
											sx={{ fontSize: 15, fontWeight: 500 }}
											noWrap
											title={
												storeInsights.heaviest
													? `${storeInsights.heaviest.store} (${storeInsights.heaviest.channel})`
													: ""
											}>
											{storeInsights.heaviest?.store || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{storeInsights.heaviest
												? `${storeInsights.heaviest.channel} · ${formatPct(
														storeInsights.heaviest.catalogPct
													)} of MRP`
												: "—"}
										</Typography>
									</Paper>
									<Paper
										elevation={0}
										sx={{
											p: 1.25,
											borderRadius: 2,
											border: "1px solid #c8e6c9",
											bgcolor: "#f1f8f4",
										}}>
										<Typography variant="caption" fontWeight={500} color={PASTEL.good}>
											Leanest discount store
										</Typography>
										<Typography
											sx={{ fontSize: 15, fontWeight: 500 }}
											noWrap
											title={
												storeInsights.leanest
													? `${storeInsights.leanest.store} (${storeInsights.leanest.channel})`
													: ""
											}>
											{storeInsights.leanest?.store || "—"}
										</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{storeInsights.leanest
												? `${storeInsights.leanest.channel} · only ${formatPct(
														storeInsights.leanest.catalogPct
													)} of MRP`
												: "—"}
										</Typography>
									</Paper>
								</Box>

								<Grid container spacing={1.5}>
									{storeInsights.byChannel.length ? (
										storeInsights.byChannel.map((ch) => (
											<Grid item xs={12} lg={6} key={ch.channel}>
												<ChartCard
													title={`${ch.channel} — top 5 stores`}
													subtitle="Ranked by discount given. Hover for full store name and metrics."
													chartHeight={Math.max(260, ch.chart.labels.length * 48 + 70)}>
													{ch.chart.labels.length ? (
														<D3BarChart
															height={Math.max(240, ch.chart.labels.length * 48 + 70)}
															layout="horizontal"
															labels={ch.chart.labels}
															labelMax={18}
															minBandSize={48}
															tooltips={ch.chart.tooltips}
															series={[
																{
																	label: "Discount given",
																	color: ch.color,
																	values: ch.chart.absorbed,
																},
															]}
															valueFormatter={formatINR}
														/>
													) : (
														<EmptyChart message={`No stores for ${ch.channel}`} />
													)}
												</ChartCard>
											</Grid>
										))
									) : (
										<Grid item xs={12}>
											<EmptyChart message="No store data for selected filters" />
										</Grid>
									)}

									{storeInsights.byChannel.map((ch) => (
										<Grid item xs={12} key={`${ch.channel}-table`}>
											<Paper
												elevation={0}
												sx={{
													p: 1.5,
													borderRadius: 2,
													border: "1px solid #e8eaed",
													overflow: "hidden",
												}}>
												<Stack
													direction="row"
													justifyContent="space-between"
													alignItems="center"
													mb={1}
													flexWrap="wrap"
													useFlexGap
													spacing={1}>
													<Typography variant="subtitle1" fontWeight={500}>
														{ch.channel} — top 5 store scorecard
													</Typography>
													<Chip
														size="small"
														label={ch.channel}
														sx={{
															height: 22,
															fontWeight: 500,
															bgcolor: `${ch.color}18`,
															color: ch.color,
														}}
													/>
												</Stack>
												<TableContainer sx={{ width: "100%" }}>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell>#</TableCell>
																<TableCell>Store</TableCell>
																<TableCell align="right">Sale</TableCell>
																<TableCell align="right">Discount</TableCell>
																<TableCell align="right">Disc % of MRP</TableCell>
																<TableCell align="right">ASP</TableCell>
																<TableCell align="right">AOV</TableCell>
																<TableCell align="right">Qty</TableCell>
																<TableCell align="right">Bills</TableCell>
																<TableCell>Hint</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{ch.stores.length ? (
																ch.stores.map((row, idx) => (
																	<TableRow key={`${ch.channel}-${row.store}-${idx}`}>
																		<TableCell>{idx + 1}</TableCell>
																		<TableCell
																			sx={{
																				maxWidth: 200,
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																				whiteSpace: "nowrap",
																			}}
																			title={row.store}>
																			{row.store}
																		</TableCell>
																		<TableCell align="right">
																			{formatINR(row.net)}
																		</TableCell>
																		<TableCell align="right">
																			{formatINR(row.absorbed)}
																		</TableCell>
																		<TableCell align="right">
																			{formatPct(row.catalogPct)}
																		</TableCell>
																		<TableCell align="right">
																			{formatINR(row.asp)}
																		</TableCell>
																		<TableCell align="right">
																			{formatINR(row.aov)}
																		</TableCell>
																		<TableCell align="right">
																			{formatNumber(row.qty)}
																		</TableCell>
																		<TableCell align="right">
																			{formatNumber(row.bills)}
																		</TableCell>
																		<TableCell>
																			<Chip
																				size="small"
																				label={row.action.label}
																				title={row.action.hint}
																				sx={{
																					height: 22,
																					fontSize: 11,
																					fontWeight: 500,
																					bgcolor: `${row.action.color}14`,
																					color: row.action.color,
																				}}
																			/>
																		</TableCell>
																	</TableRow>
																))
															) : (
																<TableRow>
																	<TableCell colSpan={11} align="center">
																		No stores for {ch.channel}
																	</TableCell>
																</TableRow>
															)}
														</TableBody>
													</Table>
												</TableContainer>
											</Paper>
										</Grid>
									))}
								</Grid>
							</Stack>
						</TabPanel>

						{/* Schemes */}
						<TabPanel value={activeTab} index={3}>
							<Stack spacing={1.5}>
								<Paper
									elevation={0}
									sx={{
										p: 1.5,
										borderRadius: 2,
										border: "1px solid #f3e5f5",
										bgcolor: "#faf5fc",
									}}>
									<Typography fontWeight={500} color={PASTEL.purple} mb={0.5}>
										Schemes in your selected date range
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Status mix, channel mix, scheme health (Keep / Tune / Cut), sale during
										each discount period, and schemes ending in the next 7 days.
									</Typography>
								</Paper>

								<Box
									sx={{
										display: "grid",
										width: "100%",
										gap: 1,
										gridTemplateColumns: {
											xs: "1fr",
											sm: "repeat(2, minmax(0, 1fr))",
											md: "repeat(4, minmax(0, 1fr))",
										},
									}}>
									{[
										{
											label: "Total schemes",
											value: formatNumber(schemes.total_schemes ?? kpis.total_schemes),
											color: PASTEL.sale,
										},
										{
											label: "Published (live now)",
											value: formatNumber(schemes.active_schemes ?? kpis.active_schemes),
											color: PASTEL.good,
										},
										{
											label: "Published (all)",
											value: formatNumber(
												schemes.published_schemes ?? kpis.published_schemes
											),
											color: PASTEL.purple,
										},
										{
											label: "Closed",
											value: formatNumber(schemes.closed_schemes),
											color: PASTEL.discount,
										},
									].map((card) => (
										<Paper
											key={card.label}
											elevation={0}
											sx={{
												p: 1.25,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												bgcolor: "#fff",
											}}>
											<Typography variant="caption" fontWeight={500} color={card.color}>
												{card.label}
											</Typography>
											<Typography sx={{ fontSize: 22, fontWeight: 500, color: "#455a64" }}>
												{card.value}
											</Typography>
										</Paper>
									))}
								</Box>

								<Paper
									elevation={0}
									sx={{
										p: 1.5,
										borderRadius: 2,
										border: "1px solid #e8eaed",
										bgcolor: "#fff",
										boxSizing: "border-box",
									}}>
									<Typography variant="subtitle2" fontWeight={500} mb={0.5}>
										Scheme health impact
									</Typography>
									<Typography variant="body2" color="text.secondary" mb={1.25}>
										Based on discount cost vs sale for overlapping schemes in this period.
									</Typography>
									<Box
										sx={{
											display: "grid",
											width: "100%",
											gap: 1,
											gridTemplateColumns: {
												xs: "1fr",
												sm: "repeat(3, minmax(0, 1fr))",
											},
										}}>
										{[
											{
												label: "Keep & grow",
												count: discountImpact.keep,
												hint: "Low discount cost",
												color: PASTEL.good,
											},
											{
												label: "Tune discount",
												count: discountImpact.tune,
												hint: "Moderate cost",
												color: PASTEL.warn,
											},
											{
												label: "Review / cut",
												count: discountImpact.cut,
												hint: "High discount burn",
												color: PASTEL.discount,
											},
										].map((row) => (
											<Box
												key={row.label}
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													gap: 1,
													py: 0.75,
													px: 1,
													borderRadius: 1.5,
													bgcolor: `${row.color}12`,
												}}>
												<Box>
													<Typography
														sx={{ fontSize: 13, fontWeight: 500, color: row.color }}>
														{row.label}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{row.hint}
													</Typography>
												</Box>
												<Typography sx={{ fontSize: 22, fontWeight: 500, color: "#455a64" }}>
													{row.count}
												</Typography>
											</Box>
										))}
									</Box>
									{discountImpact.heaviestChannel ? (
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mt: 1.5 }}>
											Heaviest channel:{" "}
											<strong>{discountImpact.heaviestChannel.channel}</strong> at{" "}
											{formatPct(discountImpact.heaviestChannel.catalog_discount_pct)} of
											MRP ({formatINR(discountImpact.heaviestChannel.absorbed_discount)}{" "}
											discount).
										</Typography>
									) : null}
									<Typography
										variant="caption"
										color="text.secondary"
										display="block"
										sx={{ mt: 1 }}>
										Cost / bill: {formatINR(discountImpact.absorbedPerBill)}.
									</Typography>
								</Paper>

								<Grid container spacing={1.5}>
									<Grid item xs={12} md={5}>
										<ChartCard
											title="Scheme status"
											subtitle="How schemes in this date range are distributed by status."
											chartHeight={280}>
											{statusPie.length ? (
												<D3DonutChart
													height={280}
													data={statusPie}
													valueFormatter={(v) => String(v)}
												/>
											) : (
												<EmptyChart message="No scheme status data for selected filters" />
											)}
										</ChartCard>
									</Grid>
									<Grid item xs={12} md={7}>
										<ChartCard
											title="Schemes by channel"
											subtitle="Count of overlapping schemes on EBO / LFS / MBO."
											chartHeight={280}>
											{schemeChannelChart.labels.length ? (
												<D3BarChart
													height={260}
													layout="horizontal"
													labels={schemeChannelChart.labels}
													labelMax={10}
													minBandSize={48}
													tooltips={schemeChannelChart.tooltips}
													series={[
														{
															label: "Schemes",
															color: PASTEL.purple,
															values: schemeChannelChart.values,
														},
													]}
													valueFormatter={(v) => String(Math.round(Number(v) || 0))}
												/>
											) : (
												<EmptyChart message="No channel scheme counts" />
											)}
										</ChartCard>
									</Grid>

									<Grid item xs={12}>
										<ChartCard
											title="Sale during each scheme window"
											subtitle="Top schemes by sale. Each bar is that scheme’s brand/channel sale in its own dates. Overlapping schemes share days — do not add bars to match Overview Net Sale."
											chartHeight={Math.max(320, schemeFrameChart.labels.length * 56 + 80)}>
											{schemeFrameChart.labels.length ? (
												<D3BarChart
													height={Math.max(300, schemeFrameChart.labels.length * 56 + 80)}
													layout="horizontal"
													labels={schemeFrameChart.labels}
													labelMax={28}
													minBandSize={60}
													tooltips={schemeFrameChart.tooltips}
													summaryMode="peak"
													series={[
														{
															label: "Net Sale",
															color: PASTEL.teal,
															values: schemeFrameChart.netSale,
														},
														{
															label: "Absorbed Discount",
															color: PASTEL.purple,
															values: schemeFrameChart.absorbed,
														},
													]}
													valueFormatter={formatINR}
												/>
											) : (
												<EmptyChart message="No discount period sale data for selected filters" />
											)}
										</ChartCard>
									</Grid>

									<Grid item xs={12}>
										<Paper
											elevation={0}
											sx={{
												p: 1.5,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												overflow: "hidden",
											}}>
											<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
												Scheme performance (sale in period)
											</Typography>
											<Typography variant="body2" color="text.secondary" mb={1}>
												Sale and discount during each scheme’s overlapping days in From–To.
											</Typography>
											<TableContainer sx={{ maxHeight: 420, width: "100%" }}>
												<Table size="small" stickyHeader>
													<TableHead>
														<TableRow>
															<TableCell>Scheme</TableCell>
															<TableCell>Channel</TableCell>
															<TableCell>Brands</TableCell>
															<TableCell>Status</TableCell>
															<TableCell>Period</TableCell>
															<TableCell align="right">Sale</TableCell>
															<TableCell align="right">Discount</TableCell>
															<TableCell align="right">Disc % of MRP</TableCell>
															<TableCell align="right">Sale / day</TableCell>
															<TableCell>Hint</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{schemePerformance.length ? (
															schemePerformance.map((row) => (
																<TableRow
																	key={`${row.scheme_id}-${row.channel}`}>
																	<TableCell
																		sx={{
																			maxWidth: 200,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.scheme_name}>
																		{row.scheme_name}
																	</TableCell>
																	<TableCell>{row.channel}</TableCell>
																	<TableCell
																		sx={{
																			maxWidth: 140,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.brandsLabel}>
																		{row.brandsLabel}
																	</TableCell>
																	<TableCell>
																		<Chip size="small" label={row.status || "—"} />
																	</TableCell>
																	<TableCell>{row.period}</TableCell>
																	<TableCell align="right">
																		{formatINR(row.net)}
																	</TableCell>
																	<TableCell align="right">
																		{formatINR(row.absorbed)}
																	</TableCell>
																	<TableCell align="right">
																		{formatPct(row.catalogPct)}
																	</TableCell>
																	<TableCell align="right">
																		{row.sale_per_day != null
																			? formatINR(row.sale_per_day)
																			: "—"}
																	</TableCell>
																	<TableCell>
																		<Chip
																			size="small"
																			label={row.action.label}
																			title={row.action.hint}
																			sx={{
																				height: 22,
																				fontSize: 11,
																				fontWeight: 500,
																				bgcolor: `${row.action.color}14`,
																				color: row.action.color,
																			}}
																		/>
																	</TableCell>
																</TableRow>
															))
														) : (
															<TableRow>
																<TableCell colSpan={11} align="center">
																	No scheme sale data for selected filters
																</TableCell>
															</TableRow>
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									</Grid>

									<Grid item xs={12} md={5}>
										<Paper
											elevation={0}
											sx={{
												p: 1.5,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												height: "100%",
												overflow: "hidden",
											}}>
											<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
												Expiring soon (next 7 days)
											</Typography>
											<Typography variant="body2" color="text.secondary" mb={1}>
												Published schemes ending within a week.
											</Typography>
											<TableContainer sx={{ maxHeight: 320, width: "100%" }}>
												<Table size="small" stickyHeader>
													<TableHead>
														<TableRow>
															<TableCell>Scheme</TableCell>
															<TableCell>Channel</TableCell>
															<TableCell>End</TableCell>
															<TableCell align="right">Days left</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{(schemes.expiring_soon || []).length ? (
															schemes.expiring_soon.map((row) => (
																<TableRow key={row.id}>
																	<TableCell
																		sx={{
																			maxWidth: 180,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.scheme_name}>
																		{row.scheme_name}
																	</TableCell>
																	<TableCell>{row.channel}</TableCell>
																	<TableCell>
																		{row.end_date
																			? moment(row.end_date).format("DD MMM YYYY")
																			: "—"}
																	</TableCell>
																	<TableCell align="right">
																		{row.days_left != null ? row.days_left : "—"}
																	</TableCell>
																</TableRow>
															))
														) : (
															<TableRow>
																<TableCell colSpan={4} align="center">
																	No schemes expiring in the next 7 days
																</TableCell>
															</TableRow>
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									</Grid>

									<Grid item xs={12} md={7}>
										<Paper
											elevation={0}
											sx={{
												p: 1.5,
												borderRadius: 2,
												border: "1px solid #e8eaed",
												overflow: "hidden",
											}}>
											<Typography variant="subtitle1" fontWeight={500} mb={0.5}>
												All schemes in range
											</Typography>
											<Typography variant="body2" color="text.secondary" mb={1}>
												Published / closed schemes whose dates overlap From–To.
											</Typography>
											<TableContainer sx={{ maxHeight: 360, width: "100%" }}>
												<Table size="small" stickyHeader>
													<TableHead>
														<TableRow>
															<TableCell>Scheme</TableCell>
															<TableCell>Channel</TableCell>
															<TableCell>Brands</TableCell>
															<TableCell>Status</TableCell>
															<TableCell>Start</TableCell>
															<TableCell>End</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{schemeList.length ? (
															schemeList.map((row) => (
																<TableRow key={row.id}>
																	<TableCell
																		sx={{
																			maxWidth: 200,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.scheme_name}>
																		{row.scheme_name}
																	</TableCell>
																	<TableCell>{row.channel}</TableCell>
																	<TableCell
																		sx={{
																			maxWidth: 140,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={row.brandsLabel}>
																		{row.brandsLabel}
																	</TableCell>
																	<TableCell>
																		<Chip size="small" label={row.status || "—"} />
																	</TableCell>
																	<TableCell>
																		{row.start_date
																			? moment(row.start_date).format("DD MMM YYYY")
																			: "—"}
																	</TableCell>
																	<TableCell>
																		{row.end_date
																			? moment(row.end_date).format("DD MMM YYYY")
																			: "—"}
																	</TableCell>
																</TableRow>
															))
														) : (
															<TableRow>
																<TableCell colSpan={6} align="center">
																	No schemes for selected filters
																</TableCell>
															</TableRow>
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									</Grid>
								</Grid>
							</Stack>
						</TabPanel>
					</Box>
				</Paper>
			)}
		</Box>
	);
}

export default DiscountReport;
